// src/BookingDetails.js
import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { notifyCustomerNewDialogMessage } from "./utils/smsGateway";
import { normalizeWorkshopSlug } from "./components/workshop/workshopCapacityUtils";
import { escapeHtml } from "./utils/format";
import SmsTilKunden from "./components/SmsTilKunden";
import KundeBanner from "./components/KundeBanner";
import Ikon from "./ny-kundesamhandling/Ikon";
import { Sidetopp } from "./ny-kundesamhandling/Skjema";
import { meldSmsSendt } from "./utils/smsStatus";
import { onsketFerdigTekst, erForTidligOnsket, kundeFerdigLinje, IKKE_SATT } from "./utils/ferdigDatoVisning";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


/* ---------------- helpers ---------------- */
function normalizePhone(input) {
  if (!input) return "";

  const raw = String(input).trim();
  const digits = raw.replace(/\D/g, "");

  if (digits.length === 8) return digits;

  if (/^\s*\+47\b/.test(raw) || /^\s*0047\b/.test(raw)) {
    const stripped = digits.replace(/^(0047|47)/, "");
    if (stripped.length === 8) return stripped;
  }

  return digits;
}

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const formatNOK = (n) =>
  new Intl.NumberFormat("no-NO", { style: "currency", currency: "NOK" }).format(n);

const getPrice = (row = {}) =>
  (row.price != null ? toNumber(row.price) : null) ??
  (row.pris != null ? toNumber(row.pris) : null) ??
  (row.totalPris != null ? toNumber(row.totalPris) : null);

const getComment = (row = {}) =>
  row.kommentar ?? row.comment ?? row.beskrivelse ?? row.description ?? "";

const BOOKINGTYPE_LABELS = {
  Dag: "Dag",
  Tidliguke: "Tidliguke",
  Senuke: "Senuke",
  Uke: "Uke",
  Fortlopende: "Fortløpende",
  Overstyrt: "Overstyrt",
  akuttbooking: "Akutt",
  chatbooking: "Chat",
};
const bookingTypeLabel = (type) => BOOKINGTYPE_LABELS[type] || type || "";

// "items" og "tjenester" på en produktlinje er normalt identiske, parallelle
// kloner av samme tjenesteutvalg (se buildNyttProdukt/buildValgteTjenester på
// kunde- og mekanikersiden) — samme rad på samme indeks i begge arrayer.
// Par dem derfor opp indeksvis: identisk innhold på samme indeks telles kun
// én gang, mens ev. avvikende innhold (eldre/uregelmessige bookinger) beholdes
// som egne rader. Dette unngår både dobbel-visning av identiske tjenester OG
// utilsiktet sammenslåing av bevisst like tjenester lagt til flere ganger.
const serviceRowKey = (row = {}) =>
  [row.slug || "", row.produktnavn || row.title || "", getPrice(row) ?? "", row.tidsbruk ?? ""].join("|");

const extractServiceRows = (prod = {}) => {
  const a = Array.isArray(prod.items) ? prod.items : [];
  const b = Array.isArray(prod.tjenester) ? prod.tjenester : [];
  const len = Math.max(a.length, b.length);
  const rows = [];
  for (let i = 0; i < len; i++) {
    const ai = a[i];
    const bi = b[i];
    if (ai && bi && serviceRowKey(ai) === serviceRowKey(bi)) {
      rows.push(ai);
    } else {
      if (ai) rows.push(ai);
      if (bi) rows.push(bi);
    }
  }
  return rows;
};

function formatDateNoShort(value) {
  if (!value) return "Ikke satt";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(value).slice(0, 10);
  }
}




function toDateTimeLocalValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function buildDefaultReminderDateTime(leveringsDato) {
  if (!leveringsDato) return "";

  const d = new Date(leveringsDato);
  if (Number.isNaN(d.getTime())) return "";

  d.setDate(d.getDate() - 1);
  d.setHours(12, 0, 0, 0);

  return toDateTimeLocalValue(d);
}

function getEarliestDeliveryDate(items = []) {
  const dates = (Array.isArray(items) ? items : [])
    .map((prod) => prod?.leveringsDato)
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b);

  return dates.length ? dates[0] : null;
}

function formatDateTimeNo(value) {
  if (!value) return "Ikke satt";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Ikke satt";

  return d.toLocaleString("no-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normSlug(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/å/g, "aa").replace(/æ/g, "ae").replace(/ø/g, "oe")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function hasElSykkelFullService(booking) {
  const TARGET = "sykkelserviceelsykkel";
  return (Array.isArray(booking?.items) ? booking.items : []).some((prod) => {
    const tjenester = (Array.isArray(prod?.items) && prod.items.length)
      ? prod.items
      : (Array.isArray(prod?.tjenester) ? prod.tjenester : []);
    return tjenester.some(
      (t) => normSlug(t?.slug || t?.produktnavn || "") === TARGET
    );
  });
}

function buildDeliveryReminderSms({ booking, portalUrl }) {
  const store = getStoreFromBooking(booking);
  const dateLines = collectBookingDateLines(booking);

  let msg = `Hei,`;
  msg += `\nDette er en påminnelse om at du har serviceinnlevering hos ${store}.`;

  if (dateLines.length) {
    msg += `\n\n${dateLines.join("\n\n")}`;
  }

  if (hasElSykkelFullService(booking)) {
    msg += `\n\nHusk å levere batterinøkkel til sykkelen.`;
  }

  if (portalUrl) {
    msg += `\n\nSe bookingdetaljer og avtal endringer her:\n${portalUrl}`;
  }

  msg += `\n\nMvh ${store}`;
  return msg;
}

function buildDeliveryReminderEmailPreview({ booking, portalUrl }) {
  const store = getStoreFromBooking(booking);
  const items = Array.isArray(booking?.items) ? booking.items : [];

  const lines = [
    "Hei!",
    "",
    "Dette er en påminnelse om at du har en serviceinnlevering hos oss. Husk å ta med produktet ditt til verkstedet på angitt dato.",
  ];

  for (const prod of items) {
    const title = [
      prod?.produktmerke || prod?.produktnavn || "Produkt",
      prod?.farge ? `(${prod.farge})` : "",
      prod?.produkttype ? `– ${prod.produkttype}` : "",
    ].filter(Boolean).join(" ");

    const tjenester = (Array.isArray(prod?.items) && prod.items.length)
      ? prod.items
      : (Array.isArray(prod?.tjenester) ? prod.tjenester : []);

    const svcLines = tjenester
      .map((t) => {
        const fb = Array.isArray(t?.foranBak) ? t.foranBak.map((v) => String(v).toLowerCase()) : [];
        return `  - ${t?.produktnavn || t?.navn || ""}${fb.length ? ` (${fb.join(" & ")})` : ""}`;
      })
      .filter(Boolean);

    lines.push("");
    lines.push(title);
    lines.push(`Verksted: ${store}`);
    if (prod?.leveringsDato) lines.push(`Leveres senest: ${formatDateNo(prod.leveringsDato)}`);
    const ferdigLinje = kundeFerdigLinje(prod, formatDateNo, "Forventet ferdig");
    if (ferdigLinje) lines.push(`${ferdigLinje.label}: ${ferdigLinje.tekst}`);
    lines.push(...svcLines);
  }

  if (hasElSykkelFullService(booking)) {
    lines.push("");
    lines.push("Husk å levere batterinøkkel til sykkelen.");
  }

  if (portalUrl) {
    lines.push("");
    lines.push(`Kundeportal: ${portalUrl}`);
  }

  lines.push("");
  lines.push(`Spørsmål? Ta kontakt med ${store}`);

  return lines.join("\n");
}

function findCreatedServiceForProduct(createdServices = [], prod = {}, fallbackIndex = 0) {
  const list = Array.isArray(createdServices) ? createdServices : [];
  if (!list.length) return null;

  const prodMerke = String(prod?.produktmerke || prod?.produktnavn || "").trim().toLowerCase();
  const prodFarge = String(prod?.farge || "").trim().toLowerCase();
  const prodType = String(prod?.produkttype || prod?.produktType || "").trim().toLowerCase();

  const exactMatch = list.find((svc) => {
    const svcMerke = String(
      svc?.produkt?.produktmerke ||
      svc?.produkt?.navn ||
      svc?.produkt?.produktnavn ||
      ""
    ).trim().toLowerCase();

    const svcFarge = String(svc?.produkt?.farge || "").trim().toLowerCase();
    const svcType = String(
      svc?.produkt?.produkttype ||
      svc?.produkt?.produktType ||
      ""
    ).trim().toLowerCase();

    return (
      svcMerke === prodMerke &&
      svcFarge === prodFarge &&
      svcType === prodType
    );
  });

  if (exactMatch) return exactMatch;

  const merkeMatch = list.find((svc) => {
    const svcMerke = String(
      svc?.produkt?.produktmerke ||
      svc?.produkt?.navn ||
      svc?.produkt?.produktnavn ||
      ""
    ).trim().toLowerCase();

    return svcMerke && svcMerke === prodMerke;
  });

  if (merkeMatch) return merkeMatch;

  return list[fallbackIndex] || null;
}

function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function sameDateValue(a, b) {
  return toDateInputValue(a) === toDateInputValue(b);
}

const extractServiceRowsWithMeta = (prod = {}) => {
  const items = Array.isArray(prod.items) ? prod.items : [];
  const tjenester = Array.isArray(prod.tjenester) ? prod.tjenester : [];
  const len = Math.max(items.length, tjenester.length);
  const rows = [];
  for (let i = 0; i < len; i++) {
    const ai = items[i];
    const bi = tjenester[i];
    if (ai && bi && serviceRowKey(ai) === serviceRowKey(bi)) {
      // Identisk par — representer som én rad, men husk begge indeksene slik
      // at sletting kan fjerne fra begge arrayene og holde dem i synk.
      rows.push({ row: ai, source: "items", index: i, mirrorIndex: i });
    } else {
      if (ai) rows.push({ row: ai, source: "items", index: i, mirrorIndex: null });
      if (bi) rows.push({ row: bi, source: "tjenester", index: i, mirrorIndex: null });
    }
  }
  return rows;
};

function buildServiceSms({ booking, service, url, status = "mottatt", comment = "", vilkarGodtatt = true }) {
  const shortRef = pickShortRef(service, booking?._id);
  const store = getStoreFromBooking(booking);
  const statusLabel = status === "mottatt" ? "mottatt" : status || "oppdatert";

  let msg = `Hei!`;
  msg += `\nDin service${shortRef ? ` (ref: ${shortRef})` : ""} er ${statusLabel} hos ${store}.`;

  if (comment && comment.trim()) {
    msg += `\n\n${comment.trim()}`;
  }

  if (url) {
    // Bookinger laget i butikk: den ansatte har informert kunden om
    // servicebetingelsene, og portalen lenker til dem (samtykke-plan.md § 2).
    msg += vilkarGodtatt
      ? `\n\nSe status og kommuniser med mekaniker her:\n${url}`
      : `\n\nSe status og servicebetingelser, og kommuniser med mekaniker her:\n${url}`;
  }

  msg += `\n\nMvh ${store}`;
  return msg;
}

// Verkstedene en booket service kan flyttes mellom. Feltene settes likt som
// bookingflyten på nettsiden (butikk, butikkSlug, verkstedSlug), så kapasitet,
// verkstedlisten og meldinger plukker opp det nye verkstedet.
const VERKSTED_VALG = [
  {
    slug: "sport1royken",
    navn: "Sport 1 Røyken",
    adresse: "Bråsetveien 3, 3440 Røyken",
  },
  {
    slug: "sport1slemmestad",
    navn: "Sport 1 Slemmestad",
    adresse: "Kirkealleen 1, 3470 Slemmestad",
  },
];

function verkstedSlugForProduct(prod = {}) {
  return normalizeWorkshopSlug(
    prod?.verkstedSlug || prod?.butikkSlug || prod?.butikk || ""
  );
}

function verkstedNavnForProduct(prod = {}) {
  const slug = verkstedSlugForProduct(prod);
  const valg = VERKSTED_VALG.find((v) => v.slug === slug);
  return valg?.navn || String(prod?.butikk || "").trim() || "Ukjent verksted";
}

function buildUpdatedBookingSms({ booking, url, changeSummary }) {
  const store = getStoreFromBooking(booking);
  const dateLines = collectBookingDateLines(booking);
  const flyttet = movedWorkshopLine(changeSummary);

  let msg = `Hei!`;
  msg += flyttet ? `\n${flyttet}` : `\nDato for din service hos ${store} er oppdatert.`;

  if (dateLines.length) {
    msg += `\n\n${dateLines.join("\n\n")}`;
  }

  if (url) {
    msg += `\n\nSe bookingdetaljer og avtal endringer her:\n${url}`;
  }

  msg += `\n\nMvh ${store}`;
  return msg;
}

function movedWorkshopLine(changeSummary) {
  if (!changeSummary?.verkstedChanged) return "";
  const valg = VERKSTED_VALG.find((v) => v.slug === changeSummary.newVerkstedSlug);
  const navn = valg?.navn || changeSummary.newVerkstedNavn || "et annet verksted";
  const produkt = changeSummary.productName ? ` for ${changeSummary.productName}` : "";
  return `Din service${produkt} er flyttet til ${navn}${valg?.adresse ? `, ${valg.adresse}` : ""}.`;
}

function buildUpdatedBookingEmail({ booking, changeSummary }) {
  const dateLines = collectBookingDateLines(booking);
  const flyttet = movedWorkshopLine(changeSummary);

  const lines = [];

  lines.push(flyttet || "Dato for din service er oppdatert.");

  if (dateLines.length) {
    lines.push(dateLines.join("\n\n"));
  }

  return lines.join("\n\n");
}

function formatDateNo(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("no-NO");
  } catch {
    return String(value).slice(0, 10);
  }
}

function collectBookingDateLines(booking) {
  const products = Array.isArray(booking?.items) ? booking.items : [];

  return products
    .map((prod) => {
      const produktnavn =
        `${prod?.produktmerke || prod?.produktnavn || "Produkt"}${prod?.farge ? ` (${prod.farge})` : ""}`;

      const levering = prod?.leveringsDato
        ? `Innlevering: ${formatDateNo(prod.leveringsDato)}`
        : null;

      const ferdigLinje = kundeFerdigLinje(prod, formatDateNo, "Forventet ferdig innen");
      const ferdig = ferdigLinje ? `${ferdigLinje.label}: ${ferdigLinje.tekst}` : null;

      const parts = [levering, ferdig].filter(Boolean);
      if (!parts.length) return null;

      return `${produktnavn}\n${parts.join("\n")}`;
    })
    .filter(Boolean);
}

// ✅ bruk butikk fra booking hvis mulig, ellers fall tilbake til cookie
function getStoreFromBooking(booking) {
  if (booking && Array.isArray(booking.items) && booking.items.length > 0) {
    const first = booking.items[0] || {};
    const fromItem = first.butikk || first.store || first.butikkNavn || null;
    if (fromItem && String(fromItem).trim()) {
      return String(fromItem).trim();
    }
  }
  // fallback: global valgt butikk (som i dag)
  return getStoreLabel();
}


function buildServiceEmail({ booking, service, url, status = "mottatt" }) {


  const lines = [];

  // ÉN statuslinje
  lines.push(
    `Vi har mottatt din service og du vil kunne hold deg oppdatert på fremdriften og kommunisere med oss, ved å trykke på knappen under.`
  );



  return lines.join("\n\n");
}







function debugSumProducts(items = []) {
  items.forEach((prod, idx) => {
    const rows = extractServiceRows(prod);
    const delsum = rows.reduce((s, r) => s + (getPrice(r) ?? 0), 0);
    console.log(
      `[sum] produkt #${idx} (${prod.produktmerke || prod.produktnavn || "Produkt"}) delsum:`,
      delsum
    );
  });
}

// Finn 5-sifret referanse for en service (fallback: siste 5 av id)
function pickShortRef(s, fallbackId) {
  const fromFields = [
    s?.ref, s?.shortRef, s?.kortref, s?.kortRef,
    s?.serviceRef,              // hvis du allerede lagrer kort ref her
    s?.service?.ref,
    s?.service?.shortRef,
    s?.service?.kortref,
  ]
    .map(v => (v == null ? "" : String(v).trim()))
    .filter(Boolean);

  // Foretrekk nøyaktig 5 siffer
  const exact5 = fromFields.find(v => /^\d{5}$/.test(v));
  if (exact5) return exact5;

  // Ellers første kort kandidat (4–6 siffer) om det finnes
  const shortish = fromFields.find(v => /^\d{4,6}$/.test(v));
  if (shortish) return shortish;

  // Siste fallback: ta de siste 5 av en id
  const id = String(fallbackId || s?._id || s?.id || "");
  return id ? id.slice(-5) : "";
}


const normStore = (s = "") =>
  String(s)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/sport\s*1/g, "sport1")
    .replace(/\s+/g, "")
    .trim();

const sameStore = (a = "", b = "") => normStore(a) === normStore(b);

// Les global valgt ansatt fra cookies/LS (nav-bar er kilden)
function readGlobalEmployee() {
  const name =
    Cookies.get("selectedEmployee") ||
    localStorage.getItem("employeeName") ||
    "";
  const id =
    Cookies.get("selectedEmployeeId") ||
    localStorage.getItem("lastStatusActorId") ||
    "";
  return { name: String(name || ""), id: String(id || "") };
}

// ✅ bruk kun cookie her, uavhengig av state
const getStoreLabel = () =>
  (Cookies.get("selectedStore") || "Ukjent").toString().trim();



/* ---------------- component ---------------- */
export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [booking, setBooking] = useState(null);
  // Hvor meldinger om bookingen går, fra serveren (kontaktFor i src/lib/kunde.js):
  // kundekortet når telefonen stemmer, ellers det som står på bookingen.
  const [kontakt, setKontakt] = useState(null);
  // Rette telefonnummeret på bookingen (PATCH /bookings/:id/telefon).
  const [rettTlfApen, setRettTlfApen] = useState(false);
  const [rettTlf, setRettTlf] = useState("");
  const [rettTlfSend, setRettTlfSend] = useState(true);
  const [rettTlfBusy, setRettTlfBusy] = useState(false);
  const [rettTlfMsg, setRettTlfMsg] = useState("");
  const [updating, setUpdating] = useState(false);

  // Kunde-kobling
const [, setCustomerExists] = useState(null);
const [, setCustomerData] = useState(null);
  const [originalCustomerId, setOriginalCustomerId] = useState(null);
  const hasCheckedCustomer = useRef(false);

  // Resultat fra splitting ved "mottatt"
  const [createdServices, setCreatedServices] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");

  // Delvis mottak
  const [selectedItemIndexes, setSelectedItemIndexes] = useState([]);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEmployeeConfirmModal, setShowEmployeeConfirmModal] = useState(false);
  const [selectedCancelIndexes, setSelectedCancelIndexes] = useState([]);

  const toggleItemSelection = (idx) => {
    setSelectedItemIndexes(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleCancelSelection = (idx) => {
    setSelectedCancelIndexes(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // For å kunne sammenligne convertedToServicesAt etter PATCH
  const prevConvertedRef = useRef(null);

  // Ansatte + aktiv ansatt (statusActor = {id,name,store})
  const [employeesForStore, setEmployeesForStore] = useState([]); // [{id,name,store}]
  const [statusActor, setStatusActor] = useState(null);
  const [statusComment, setStatusComment] = useState("");

  // Vi starter tomt og hydrerer synkront ved mount/route
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [storeName, setStoreName] = useState(Cookies.get("selectedStore") || "Ukjent");

  const [bookingMessageText, setBookingMessageText] = useState("");
const [bookingMessageBusy, setBookingMessageBusy] = useState(false);
const [bookingMessageError, setBookingMessageError] = useState("");

const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
const [bookingMessageCount, setBookingMessageCount] = useState(0);
const [unreadBookingCustomerCount, setUnreadBookingCustomerCount] = useState(0);
const [bookingImagesOpen, setBookingImagesOpen] = useState(false);

const [sendDeliveryReminderSms, setSendDeliveryReminderSms] = useState(true);
const [sendDeliveryReminderEmail, setSendDeliveryReminderEmail] = useState(true);
const [deliveryReminderDateTime, setDeliveryReminderDateTime] = useState("");

const [deliveryReminderOpen, setDeliveryReminderOpen] = useState(false);
const [deliveryReminderSaving, setDeliveryReminderSaving] = useState(false);

const [deliveryReminderNotice, setDeliveryReminderNotice] = useState("");
const [detailsSectionOpen, setDetailsSectionOpen] = useState(false);


  // 🔔 Utsendelse ved mottatt
  const [sendEmailOnReceived, setSendEmailOnReceived] = useState(true); // ✅ default på
  const [sendSmsOnReceived, setSendSmsOnReceived] = useState(true);     // ✅ default på (var false)
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState("");

  const [editProductModalOpen, setEditProductModalOpen] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState(null);
  const [editProduktmerke, setEditProduktmerke] = useState("");
  const [editFarge, setEditFarge] = useState("");
    const [editKommentar, setEditKommentar] = useState("");
      const [editLeveringsDato, setEditLeveringsDato] = useState("");
  const [editForventetFerdigDato, setEditForventetFerdigDato] = useState("");
  const [editOnsketFerdigDato, setEditOnsketFerdigDato] = useState("");
  const [editVerkstedSlug, setEditVerkstedSlug] = useState("");

  const [sendUpdatedSms, setSendUpdatedSms] = useState(true);
const [sendUpdatedEmail, setSendUpdatedEmail] = useState(true);
const [updatedNotifyBusy, setUpdatedNotifyBusy] = useState(false);

const [dateChangePending, setDateChangePending] = useState(false);
const [lastDateChangeSummary, setLastDateChangeSummary] = useState(null);

const saveDeliveryReminderSettings = async () => {
  if (!booking?._id) return;

  const shouldSend = !!sendDeliveryReminderSms || !!sendDeliveryReminderEmail;

  if (shouldSend && !deliveryReminderDateTime) {
    setNotifyMsg("Velg sendetidspunkt for innleveringsvarsel.");
    return;
  }

  setDeliveryReminderSaving(true);
  setNotifyMsg("");
  setDeliveryReminderNotice("");

  try {
    const payload = {
      deliveryReminder: {
        enabled: shouldSend,
        sendAt: shouldSend ? new Date(deliveryReminderDateTime).toISOString() : null,
        sms: !!sendDeliveryReminderSms,
        email: !!sendDeliveryReminderEmail,
      },
    };

    const r = await fetch(`${API_BASE_URL}/bookings/${booking._id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await r.json();

    if (!r.ok) {
      throw new Error(data?.message || "Kunne ikke lagre innleveringsvarsel");
    }

    setBooking((prev) => ({
      ...data.booking,
      portalUrl: data.portalUrl || prev?.portalUrl || "",
    }));

    if (shouldSend) {
      const channels = [
        sendDeliveryReminderSms ? "SMS" : null,
        sendDeliveryReminderEmail ? "e-post" : null,
      ]
        .filter(Boolean)
        .join(" og ");

      setNotifyMsg("Innleveringsvarsel lagret.");
      setDeliveryReminderNotice(
        `Aktivt innleveringsvarsel: ${formatDateTimeNo(deliveryReminderDateTime)} via ${channels}.`
      );
    } else {
      setNotifyMsg("Innleveringsvarsel slått av.");
      setDeliveryReminderNotice("");
    }
  } catch (e) {
    console.error("Feil ved lagring av innleveringsvarsel:", e);
    setNotifyMsg("Kunne ikke lagre innleveringsvarsel.");
    setDeliveryReminderNotice("");
  } finally {
    setDeliveryReminderSaving(false);
  }
};

  const setActiveEmployee = useCallback((emp, reason = "") => {
    if (!emp) return false;
    const store = Cookies.get("selectedStore") || "Ukjent";

    // vis i UI (men IKKE skriv cookies her hvis endringen kommer utenfra)
    setSelectedEmployeeName(emp.name);
    setStatusActor({ id: emp.id, name: emp.name, store });

    return true;
  }, []);

  const fetchCreatedServicesForBooking = useCallback(async (bookingId) => {
  if (!bookingId) return;

  try {
    const r = await fetch(`${API_BASE_URL}/workshop-services?bookingId=${bookingId}`);
    if (!r.ok) throw new Error("Kunne ikke hente opprettede service-ordre");

    const data = await r.json();
    const services = Array.isArray(data) ? data : [];

    setCreatedServices(services);
  } catch (e) {
    console.error("Feil ved henting av opprettede services:", e);
  }
}, []);

  // Skriver *kun* når bruker manuelt endrer i dette skjemaet
  const persistEmployeeSelection = useCallback((emp) => {
    Cookies.set("selectedEmployee", emp.name);
    Cookies.set("selectedEmployeeId", String(emp.id || ""));
    localStorage.setItem("employeeName", emp.name);
    localStorage.setItem("lastStatusActorId", String(emp.id || ""));

    try {
      window.dispatchEvent(new CustomEvent("employee:changed", { detail: emp }));
      const ch = new BroadcastChannel("employee-sync");
      ch.postMessage({ type: "employee:changed", emp });
      ch.close();
    } catch { }
  }, []);

  // 🔹 Hydrer fra global (cookies/LS) før første paint + på ruteendring
  useLayoutEffect(() => {
    const store = Cookies.get("selectedStore") || "Ukjent";
    const { name, id } = readGlobalEmployee();

    if (store !== storeName) setStoreName(store);
    if (name) setSelectedEmployeeName(name);

    if (name && (!statusActor || statusActor.name !== name)) {
      // midlertidig actor; byttes ut når employees-lista lastes
      setStatusActor({ id: id || null, name, store });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Hold store i sync
  useEffect(() => {
    const readStore = () => {
      const s = Cookies.get("selectedStore") || "Ukjent";
      if (s !== storeName) setStoreName(s);
    };
    const onFocus = () => readStore();
    const onVis = () => { if (document.visibilityState === "visible") readStore(); };
    const onStorage = (e) => { if (e.key === "selectedStore") readStore(); };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("storage", onStorage);
    const t = setInterval(readStore, 400);
    readStore();
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, [storeName]);

  useEffect(() => {
  if (!booking?._id) return;
  const isReceived = String(booking.status || "").toLowerCase() === "mottatt";
  const hasPartialConversions = (booking.convertedItemIndexes || []).length > 0;
  if (!isReceived && !hasPartialConversions) return;
  fetchCreatedServicesForBooking(booking._id);
}, [booking?._id, booking?.status, booking?.convertedItemIndexes, fetchCreatedServicesForBooking]);

  useEffect(() => {
  if (!Array.isArray(booking?.messages)) {
    setBookingMessageCount(0);
    setUnreadBookingCustomerCount(0);
    return;
  }

  const customerMsgs = booking.messages.filter((m) => m.from === "customer");
  setBookingMessageCount(customerMsgs.length);

  const unread = customerMsgs.filter((m) => !m.readAt);
  setUnreadBookingCustomerCount(unread.length);
}, [booking?.messages]);

  // Speil ansatt fra global kilde fortløpende (fokus/visibility/storage)
  useEffect(() => {
    const low = (s) => String(s || "").trim().toLowerCase();
    const readName = () => {
      const { name } = readGlobalEmployee();
      if (name && low(name) !== low(selectedEmployeeName)) {
        setSelectedEmployeeName(name);
      }
    };
    const onFocus = () => readName();
    const onVis = () => { if (document.visibilityState === "visible") readName(); };
    const onStorage = (e) => {
      if (e.key === "employeeName" || e.key === "selectedEmployee" || e.key === "selectedEmployeeId") {
        readName();
      }
    };

    readName();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("storage", onStorage);
    const t = setInterval(readName, 500);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, [selectedEmployeeName]);

  // Ghost-option: sørg for at valgt navn alltid finnes i select-options inntil ekte liste er klar
  useEffect(() => {
    const low = (s) => String(s || "").trim().toLowerCase();
    if (!selectedEmployeeName) return;

    const exists = employeesForStore.some(e => low(e.name) === low(selectedEmployeeName));
    if (exists) {
      // oppdater statusActor hvis den peker på et annet ID/navn
      const match = employeesForStore.find(e => low(e.name) === low(selectedEmployeeName));
      if (match && (statusActor?.id !== match.id || low(statusActor?.name) !== low(match.name))) {
        setActiveEmployee(match, "reconcile:match-after-list");
      }
      return;
    }

    // ingen match i liste -> lag midlertidig entry
    const { id } = readGlobalEmployee();
    const ghostId = id || `ghost:${selectedEmployeeName}`;

    setEmployeesForStore(prev => {
      if (prev.some(e => low(e.name) === low(selectedEmployeeName))) return prev;
      return [{ id: ghostId, name: selectedEmployeeName, store: storeName }, ...prev];
    });

    if (!statusActor || low(statusActor.name) !== low(selectedEmployeeName)) {
      setStatusActor({ id: ghostId, name: selectedEmployeeName, store: storeName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeName, storeName, employeesForStore]);

  // Reager på sanntid fra navbar (CustomEvent + BroadcastChannel)
  useEffect(() => {
    const low = (s) => String(s || "").trim().toLowerCase();
    const handleImmediate = (emp) => {
      if (!emp) return;
      const match =
        employeesForStore.find((e) => String(e.id) === String(emp.id)) ||
        employeesForStore.find((e) => low(e.name) === low(emp.name)) ||
        employeesForStore.find((e) => low(e.name).includes(low(emp.name)));
      if (match) {
        setActiveEmployee(match, "event:employee:changed");
      } else {
        setSelectedEmployeeName(emp.name); // liste kommer snart
      }
    };
    const onCustom = (e) => handleImmediate(e.detail);

    window.addEventListener("employee:changed", onCustom);
    let ch = null;
    try {
      ch = new BroadcastChannel("employee-sync");
      ch.onmessage = (msg) => {
        if (msg?.data?.type === "employee:changed") handleImmediate(msg.data.emp);
      };
    } catch { }

    return () => {
      window.removeEventListener("employee:changed", onCustom);
      if (ch) ch.close();
    };
  }, [employeesForStore, setActiveEmployee]);

  /* ---------- fetch booking ---------- */
  useEffect(() => {
    const fetchOne = async () => {
      try {
        console.groupCollapsed("[BookingDetail] GET /bookings/:id");
        console.log("URL:", `${API_BASE_URL}/bookings/${id}`);
        const r = await fetch(`${API_BASE_URL}/bookings/${id}`);
        if (!r.ok) throw new Error("Kunne ikke hente booking");
        const data = await r.json();
        console.log("Hentet booking:", data);
setBooking({
  ...data.booking,
  portalUrl: data.portalUrl || "",
});
        prevConvertedRef.current = data?.booking?.convertedToServicesAt ?? null;
        debugSumProducts(data?.booking?.items || []);
        console.groupEnd();
      } catch (e) {
        console.error(e);
      }
    };
    fetchOne();
  }, [id]);

        useEffect(() => {
  const dr = booking?.deliveryReminder;
  if (!dr) return;

  // Hvis varselet ikke er sendt ennå → alltid default til true (skjema-default er false)
  const alreadySent = !!dr.sentAt;
  setSendDeliveryReminderSms(alreadySent ? !!dr.sms : true);
  setSendDeliveryReminderEmail(alreadySent ? !!dr.email : true);

  if (dr.sendAt) {
    setDeliveryReminderDateTime(toDateTimeLocalValue(dr.sendAt));
  }
}, [booking?.deliveryReminder]);

useEffect(() => {
  if (!booking?.items?.length) return;

  const earliestDelivery = getEarliestDeliveryDate(booking.items);
  if (!earliestDelivery) return;

  const suggested = buildDefaultReminderDateTime(earliestDelivery);

  setDeliveryReminderDateTime((prev) => prev || suggested);
}, [booking?.items]);

const autoReminderActivatedRef = useRef(false);

useEffect(() => {
  if (!booking?._id) return;
  if (autoReminderActivatedRef.current) return;
  if (String(booking.status || "").toLowerCase() !== "booking") return;
  if (booking.deliveryReminder?.enabled) return;
  if (booking.deliveryReminder?.sentAt) return;

  const earliestDelivery = getEarliestDeliveryDate(booking.items);
  if (!earliestDelivery) return;
  if (earliestDelivery <= new Date()) return;

  autoReminderActivatedRef.current = true;

  const d = new Date(earliestDelivery);
  d.setDate(d.getDate() - 1);
  d.setHours(12, 0, 0, 0);

  fetch(`${API_BASE_URL}/bookings/${booking._id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      deliveryReminder: { enabled: true, sendAt: d.toISOString(), sms: true, email: true },
    }),
  })
    .then((r) => r.json())
    .then((data) => {
      if (data?.booking) {
        setBooking((prev) => ({
          ...data.booking,
          portalUrl: prev?.portalUrl || "",
        }));
      }
    })
    .catch(() => { autoReminderActivatedRef.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [booking?._id, booking?.status, booking?.deliveryReminder?.enabled]);

  /* ---------- hent ansatte fra API + cache per butikk ---------- */
  useEffect(() => {
    if (!storeName) return;

    // blank ut ved butikkskifte for å tvinge refetch
    setEmployeesForStore([]);

    const setFromArray = (arr) => {
      const ok = Array.isArray(arr) ? arr : [];
      setEmployeesForStore(ok);
    };

    // 1) Cache først
    try {
      const raw = localStorage.getItem(`employees:${storeName}`);
      const cached = raw ? JSON.parse(raw) : [];
      if (Array.isArray(cached) && cached.length) {
        setFromArray(cached);
      }
    } catch { }

    // 2) Hent fra API, filtrer, cache
    (async () => {
      try {
        const url = `${API_BASE_URL}/employees`;
        console.log("[BookingDetail] GET", url);
        const res = await fetch(url);
        if (!res.ok) throw new Error("Kunne ikke hente ansatte");

        const payload = await res.json();
        const rawList = Array.isArray(payload) ? payload : payload?.employees || [];
        console.log("[BookingDetail] employees payload:", payload);

        const list = (rawList || []).map((e) => ({
          id: e._id || e.id,
          name: e.navn || e.name || e.fullName || e.displayName || "",
          store: e.butikk || e.store || e.location || "",
        }));

        const matched = list.filter(
          (e) =>
            sameStore(e.store, storeName) ||
            /begge\s*butikker/i.test(String(e.store || ""))
        );
        const noStore = list.filter((e) => !e.store || !String(e.store).trim());
        const finalList = matched.length ? [...matched, ...noStore] : list;

        const cleaned = finalList.filter((x) => x.id && x.name);

        setEmployeesForStore(cleaned);
        localStorage.setItem(`employees:${storeName}`, JSON.stringify(cleaned));

        // Ikke skriv cookies her — reconcile-effektene tar seg av setActiveEmployee
      } catch (err) {
        console.error("Feil ved henting av ansatte:", err);
        if (!employeesForStore.length) setEmployeesForStore([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName]);

  const setQuoteEnabled = useCallback(async (serviceId, enabled) => {
    if (!serviceId) return false;
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quote: { enabled: !!enabled } }),
      });
      return r.ok;
    } catch (e) {
      console.warn("[BookingDetail] setQuoteEnabled feilet:", e);
      return false;
    }
  }, []);

  /* ---------- customer helpers ---------- */
  const fetchCustomerByPhone = useCallback(async (rawPhone) => {
    const normalized = normalizePhone(rawPhone);
    if (!normalized) return null;

    try {
      const url = `${API_BASE_URL}/customers/by-phone/${normalized}`;
      console.log("[BookingDetail] GET", url);
      const res = await fetch(url);
      if (res.status === 404) {
        console.log("[BookingDetail] by-phone: 404 Not Found");
      } else if (res.ok) {
        const c = await res.json();
        console.log("[BookingDetail] by-phone MATCH:", c);
        return c;
      }
    } catch (e) {
      console.warn("[BookingDetail] by-phone feilet, prøver fallback", e);
    }

    try {
      const url = `${API_BASE_URL}/customers?phoneNumber=${encodeURIComponent(rawPhone)}`;
      console.log("[BookingDetail] GET fallback", url);
      const res = await fetch(url);
      if (res.ok) {
        const list = await res.json();
        const match = (list || []).find(
          (c) => normalizePhone(c.phoneNumber) === normalized
        );
        console.log("[BookingDetail] fallback MATCH:", match);
        return match || null;
      }
    } catch (e) {
      console.error("Feil ved customers-søk:", e);
    }
    return null;
  }, []);

  const markAllBookingCustomerMessagesAsRead = useCallback(async () => {
  if (!booking?._id) return;

  try {
    const r = await fetch(
      `${API_BASE_URL}/bookings/${booking._id}/messages/mark-read`,
      { method: "POST" }
    );

    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      console.error("markAllBookingCustomerMessagesAsRead failed:", body);
      return;
    }

    const messages = Array.isArray(body.messages)
      ? body.messages
      : booking.messages || [];

    setBooking((prev) =>
      prev
        ? {
            ...prev,
            messages,
          }
        : prev
    );
  } catch (e) {
    console.error("markAllBookingCustomerMessagesAsRead error:", e);
  }
}, [booking?._id, booking?.messages]);

const toggleBookingMessageUnread = async (msg) => {
  if (!booking?._id || !msg?._id) return;

  // Hvis melding er ulest -> marker som lest lokalt
  if (!msg.readAt) {
    setBooking((prev) => ({
      ...prev,
      messages: prev.messages.map((x) =>
        x._id === msg._id ? { ...x, readAt: new Date().toISOString() } : x
      ),
    }));
    return;
  }

  // Hvis melding er lest -> marker som ulest via backend
  try {
    const r = await fetch(
      `${API_BASE_URL}/bookings/${booking._id}/messages/${msg._id}/mark-unread`,
      { method: "POST" }
    );

    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      console.error("toggleBookingMessageUnread failed:", body);
      return;
    }

    setBooking((prev) =>
      prev
        ? {
            ...prev,
            messages: Array.isArray(body.messages) ? body.messages : prev.messages,
          }
        : prev
    );
  } catch (e) {
    console.error("toggleBookingMessageUnread error:", e);
  }
};

  const createCustomer = useCallback(async (customerInfo) => {
    if (!customerInfo?.firstName || !customerInfo?.lastName || !customerInfo?.phoneNumber) {
      console.error("Mangler kundeinfo:", customerInfo);
      return null;
    }
    const payload = {
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      phoneNumber: customerInfo.phoneNumber,
      email: customerInfo.email || "",
    };
    const url = `${API_BASE_URL}/customers`;
    console.log("[BookingDetail] POST", url, "payload:", payload);
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.error("Kunne ikke opprette kunde:", await res.text());
      return null;
    }
    const out = await res.json();
    console.log("[BookingDetail] Opprettet kunde:", out);
    return out;
  }, []);

  const linkBookingToCustomer = useCallback(async (bookingId, customerId) => {
    const url = `${API_BASE_URL}/bookings/${bookingId}/update-original-customer`;
    console.log("[BookingDetail] PATCH", url, "payload:", { originalCustomerId: customerId });
    const r = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ originalCustomerId: customerId }),
    });
    if (!r.ok) throw new Error("Kunne ikke oppdatere booking med originalCustomerId");
    const data = await r.json();
    console.log("[BookingDetail] Oppdatert booking med originalCustomerId:", data);
setBooking((prev) => ({
  ...data.booking,
  portalUrl: data.portalUrl || prev?.portalUrl || "",
}));
  }, []);

  /* ---------- kontakt: hentes på nytt når kontaktinfo på bookingen endres ---------- */
  const kontaktNokkel = `${booking?.customer?.phoneNumber || ""}|${booking?.customer?.email || ""}|${booking?.originalCustomerId || ""}`;
  useEffect(() => {
    if (!booking?._id) return;
    let avbrutt = false;
    fetch(`${API_BASE_URL}/bookings/${booking._id}/kontakt`)
      .then((r) => (r.ok ? r.json() : null))
      .then((k) => { if (!avbrutt) setKontakt(k); })
      .catch(() => { if (!avbrutt) setKontakt(null); });
    return () => { avbrutt = true; };
  }, [booking?._id, kontaktNokkel]);

  // Mottakeren for meldinger: bookingens kunde med telefon/e-post fra kontakt.
  const medKontakt = useCallback(
    (c = {}) => (kontakt ? { ...c, phoneNumber: kontakt.telefon || "", email: kontakt.epost || "" } : c),
    [kontakt]
  );

  // Ansatt retter telefonnummeret: bookingen får nummeret og kobles til kundekortet
  // for det nummeret på serveren. Kan sende bookingbekreftelsen til det nye nummeret.
  const lagreRettTelefon = async (e) => {
    e.preventDefault();
    if (!booking?._id) return;
    setRettTlfBusy(true);
    setRettTlfMsg("");
    try {
      const r = await fetch(`${API_BASE_URL}/bookings/${booking._id}/telefon`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefon: rettTlf, sendBekreftelse: rettTlfSend }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "Kunne ikke rette nummeret");
      setBooking((prev) => ({ ...data.booking, portalUrl: prev?.portalUrl || "" }));
      setKontakt(data.kontakt || null);
      setOriginalCustomerId(data.booking?.originalCustomerId || null);
      setRettTlfApen(false);
      setRettTlf("");
      setRettTlfMsg(rettTlfSend ? "Nummeret er rettet, og bekreftelsen er sendt dit." : "Nummeret er rettet.");
    } catch (err) {
      setRettTlfMsg(err.message);
    } finally {
      setRettTlfBusy(false);
    }
  };

  /* ---------- one-time customer check ---------- */
  useEffect(() => {
    const run = async () => {
      if (!booking?.customer?.phoneNumber) return;
      if (hasCheckedCustomer.current) return;
      hasCheckedCustomer.current = true;

      console.groupCollapsed("[BookingDetail] Kunde-kobling");
      try {
        const found = await fetchCustomerByPhone(booking.customer.phoneNumber);
        if (found?._id) {
          setCustomerExists(true);
          setCustomerData(found);
          setOriginalCustomerId(found._id);
          await linkBookingToCustomer(booking._id, found._id);
          console.log("Kunde funnet og koblet:", found._id);
          console.groupEnd();
          return;
        }

        const created = await createCustomer(booking.customer);
        if (created?._id) {
          setCustomerExists(true);
          setCustomerData(created);
          setOriginalCustomerId(created._id);
          await linkBookingToCustomer(booking._id, created._id);
          console.log("Kunde opprettet og koblet:", created._id);
        } else {
          setCustomerExists(false);
          console.warn("Kunde ikke funnet/kunne ikke opprettes.");
        }
      } catch (e) {
        console.error("Feil ved kundesjekk/opprett:", e);
        setCustomerExists(false);
      } finally {
        console.groupEnd();
      }
    };
    run();
  }, [booking, fetchCustomerByPhone, createCustomer, linkBookingToCustomer]);

  // Opprett/roter kundelenke for EN workshop-service
  const createOrRotateCustomerLinkFor = useCallback(
    async (serviceId, { rotate = false } = {}) => {
      if (!serviceId) return null;

      const toUrl = (data) => {
        const token =
          data?.token || data?.portal?.token || data?.data?.token || null;
        let directUrl =
          data?.url || data?.portal?.url || data?.data?.url || null;

        // 1) Har vi token? Bygg alltid ny URL med riktig domene/stig
        if (token) {
          return `https://status.roykensykkelservice.no/svc/${token}`;
        }

        // 2) Fallback: hvis vi bare har en url, forsøk å flytte den over på riktig domene
        if (directUrl) {
          try {
            const u = new URL(directUrl);
            if (u.hostname !== "status.roykensykkelservice.no") {
              return `https://status.roykensykkelservice.no${u.pathname}${u.search || ""
                }`;
            }
          } catch {
            // hvis URL() feiler, bare bruk originalen
          }
          return directUrl;
        }

        return null;
      };


      const attempts = [
        { method: "POST", path: `/workshop-services/${serviceId}/portal-link${rotate ? "?rotate=1" : ""}` },
        { method: "POST", path: `/workshop-services/${serviceId}/portal${rotate ? "?rotate=1" : ""}` },
        { method: "PATCH", path: `/workshop-services/${serviceId}`, body: { portal: { enabled: true, rotate: !!rotate } } },
      ];

      let lastErr = null;
      for (const a of attempts) {
        try {
          const r = await fetch(`${API_BASE_URL}${a.path}`, {
            method: a.method,
            headers: a.body ? { "Content-Type": "application/json" } : undefined,
            body: a.body ? JSON.stringify(a.body) : undefined,
          });

          if (!r.ok) {
            if (r.status === 404) continue;
            lastErr = new Error(`HTTP ${r.status}`);
            continue;
          }

          const data = await r.json().catch(() => ({}));
          const url = toUrl(data);
          if (url) {
            // Ved booking -> start med tilbud AV (samme praksis som tidligere)
            setQuoteEnabled(serviceId, false).catch(() => { });
            return url;
          }
          lastErr = new Error("Respons mangler både url og token");
        } catch (e) {
          lastErr = e;
        }
      }

      console.warn("[BookingDetail] Klarte ikke å opprette kundelenke for service", serviceId, lastErr);
      return null;
    },
    [setQuoteEnabled]
  );


  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const waitForServiceLinks = useCallback(
    async ({ bookingId, preferIds = [], tries = 8, delayMs = 400 }) => {
      let lastItems = [];
      for (let i = 0; i < tries; i++) {
        try {
          const r = await fetch(`${API_BASE_URL}/workshop-services?bookingId=${bookingId}`);
          const all = r.ok ? await r.json() : [];
          const subset = Array.isArray(all)
            ? (preferIds.length ? all.filter(s => preferIds.includes(s._id || s.id)) : all)
            : [];

          // id -> { id, url, service }
          const byId = new Map();
          subset.forEach(s => {
            const id = s._id || s.id;
            const url = s?.portal?.token ? `https://status.roykensykkelservice.no/svc/${s.portal.token}` : null;
            if (id && url) byId.set(String(id), { id: String(id), url, service: s });
          });

          // Opprett manglende lenker
          const needCreate = subset
            .map(s => s._id || s.id)
            .filter(Boolean)
            .filter(id => !byId.has(String(id)));

          if (needCreate.length) {
            const made = await Promise.all(
              needCreate.map(sid => createOrRotateCustomerLinkFor(sid, { rotate: false }))
            );
            needCreate.forEach((id, idx) => {
              const url = made[idx];
              if (url) {
                const svc = subset.find(s => (s._id || s.id) === id) || null;
                byId.set(String(id), { id: String(id), url, service: svc });
              }
            });
          }

          // Rekkefølge: preferIds først hvis oppgitt
          const order = preferIds.length ? preferIds.map(String) : Array.from(byId.keys());
          const items = order.map(id => byId.get(String(id))).filter(Boolean);

          lastItems = items;

          const expected = preferIds.length || subset.length;
          if (expected > 0 && items.length >= expected) return items;
        } catch (e) {
          console.warn("[waitForServiceLinks] forsøk feilet:", e);
        }
        await sleep(delayMs);
      }
      return lastItems;
    },
    [createOrRotateCustomerLinkFor]
  );



  /* ---------- NOTIFICATION HELPERS (SMS & e-post) ---------- */
  async function sendStatusEmail({
    to,
    customerName,
    plainText,
    storeLocation,
    firstUrl,
    status = "mottatt",
    serviceRef,                 // 👈 ny
    includeStatusIntro = false, // 👈 ny – vi vil slå AV intro
    includeRefLine = false,     // 👈 ny – vi vil slå AV "Ref:"-linjen
  }) {
    if (!to) {
      setNotifyMsg("Kan ikke sende e-post: mangler e-postadresse.");
      return false;
    }
    setEmailBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/email/workshop-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to,
          customerName,
          status,                 // 👈 send status eksplisitt
          serviceRef,            // 👈 send korrekt kortref per service
          customerLink: firstUrl || "",
          plainStatusText: plainText,
          storeLocation,
          includeStatusIntro,    // 👈 flagg til backend
          includeRefLine,        // 👈 flagg til backend
        }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        console.error("E-postfeil:", r.status, text);
        setNotifyMsg("Kunne ikke sende e-post.");
        return false;
      }
      return true;
    } catch (e) {
      console.error("E-post exception:", e);
      setNotifyMsg("En feil oppstod ved sending av e-post.");
      return false;
    } finally {
      setEmailBusy(false);
    }
  }

  const sendUpdatedBookingEmail = useCallback(
  async ({ to, customerName, plainText, storeLocation, customerLink }) => {
    if (!to) {
      setNotifyMsg("Kan ikke sende e-post: mangler e-postadresse.");
      return false;
    }

    setEmailBusy(true);

    try {
      const r = await fetch(`${API_BASE_URL}/email/booking-updated`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to,
          customerName,
          plainText,
          storeLocation,
          customerLink: customerLink || "",
        }),
      });

      if (!r.ok) {
        const text = await r.text().catch(() => "");
        console.error("Booking updated e-postfeil:", r.status, text);
        setNotifyMsg("Kunne ikke sende oppdatert e-post.");
        return false;
      }

      return true;
    } catch (e) {
      console.error("Booking updated e-post exception:", e);
      setNotifyMsg("En feil oppstod ved sending av oppdatert e-post.");
      return false;
    } finally {
      setEmailBusy(false);
    }
  },
  []
);

const sendSms = useCallback(async ({ phone, message }) => {
  if (!phone) {
    setNotifyMsg("Kan ikke sende SMS: mangler telefonnummer.");
    return false;
  }

  const tel = normalizePhone(phone);
  const melding = String(message || "").trim();
  if (!melding) return false;

  setSmsBusy(true);

  try {
    // Serveren arkiverer SMS-en (arkiver: true), se POST /sms/send.
    const resp = await fetch(`${API_BASE_URL}/sms/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tel,
        message: melding,
        arkiver: true,
        kundeNavn:
          `${booking?.customer?.firstName || ""} ${booking?.customer?.lastName || ""}`.trim() ||
          "Kunde",
        butikkid: parseInt(Cookies.get("butikkid"), 10) || 0,
        ...(booking?.originalCustomerId && { customerId: String(booking.originalCustomerId) }),
      }),
    });

    if (!resp.ok) {
      console.error("SMS-feil:", resp.status);
      setNotifyMsg("Kunne ikke sende SMS.");
      return false;
    }

    meldSmsSendt();
    return true;
  } catch (err) {
    console.error("Feil ved SMS:", err);
    setNotifyMsg("En feil oppstod ved sending av SMS.");
    return false;
  } finally {
    setSmsBusy(false);
  }
}, [booking]);

const sendUpdatedBookingNotification = useCallback(async () => {
  if (!booking) return;

  const cust = medKontakt(booking.customer || {});
  const doSms = !!sendUpdatedSms && !!cust.phoneNumber;
  const doEmail = !!sendUpdatedEmail && !!cust.email;

  if (!doSms && !doEmail) {
    setNotifyMsg("Ingen meldinger valgt eller kunden mangler kontaktinfo.");
    return;
  }

  setUpdatedNotifyBusy(true);
  setNotifyMsg("");

  try {
    const bookingPortalUrl =
      booking?.portalUrl ||
      (booking?.portal?.token
        ? `${CUSTOMER_PORTAL_BASE}/bookingportal/${booking.portal.token}`
        : "");

    let anySent = false;

    if (doSms) {
      const smsOk = await sendSms({
        phone: cust.phoneNumber,
        message: buildUpdatedBookingSms({
          booking,
          url: bookingPortalUrl,
          changeSummary: lastDateChangeSummary,
        }),
      });
      anySent = anySent || smsOk;
    }

    if (doEmail) {
      const emailOk = await sendUpdatedBookingEmail({
        to: cust.email,
        customerName: `${cust.firstName || ""} ${cust.lastName || ""}`.trim(),
        plainText: buildUpdatedBookingEmail({ booking, changeSummary: lastDateChangeSummary }),
        storeLocation: getStoreFromBooking(booking),
        customerLink: bookingPortalUrl,
      });
      anySent = anySent || emailOk;
    }

    if (anySent) {
      setDateChangePending(false);
      setLastDateChangeSummary(null);
      setNotifyMsg("Oppdatert bookinginfo sendt til kunden.");
    } else {
      setNotifyMsg("Klarte ikke å sende oppdatert bookinginfo.");
    }
  } catch (err) {
    console.error("Feil ved sending av oppdatert bookinginfo:", err);
    setNotifyMsg("Feil ved sending av oppdatert bookinginfo.");
  } finally {
    setUpdatedNotifyBusy(false);
  }
}, [
  booking,
  medKontakt,
  lastDateChangeSummary,
  sendUpdatedSms,
  sendUpdatedEmail,
  sendSms,
  sendUpdatedBookingEmail,
]);

  const removeServiceFromBooking = async ({ productIndex, source, serviceIndex, mirrorIndex }) => {
    if (!booking?.items?.[productIndex]) return;

    const currentProduct = booking.items[productIndex];
    const currentRows = extractServiceRows(currentProduct);

    if (currentRows.length <= 1) {
      alert("Du kan ikke slette den siste tjenesten fra dette produktet.");
      return;
    }

    const ok = window.confirm("Vil du slette denne tjenesten fra bookingen?");
    if (!ok) return;

    setUpdating(true);
    setNotifyMsg("");
    setStatusMessage("");

    try {
      const updatedItems = booking.items.map((prod, idx) => {
        if (idx !== productIndex) return prod;

        const nextProd = { ...prod };

        if (source === "items" && Array.isArray(nextProd.items)) {
          nextProd.items = nextProd.items.filter((_, i) => i !== serviceIndex);
          if (mirrorIndex != null && Array.isArray(nextProd.tjenester)) {
            nextProd.tjenester = nextProd.tjenester.filter((_, i) => i !== mirrorIndex);
          }
        }

        if (source === "tjenester" && Array.isArray(nextProd.tjenester)) {
          nextProd.tjenester = nextProd.tjenester.filter((_, i) => i !== serviceIndex);
          if (mirrorIndex != null && Array.isArray(nextProd.items)) {
            nextProd.items = nextProd.items.filter((_, i) => i !== mirrorIndex);
          }
        }

        return nextProd;
      });

      const r = await fetch(`${API_BASE_URL}/bookings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: updatedItems }),
      });

      const data = await r.json();

      if (!r.ok) {
        throw new Error(data?.message || "Kunne ikke slette tjenesten");
      }

      if (data?.booking) {
setBooking((prev) => ({
  ...data.booking,
  portalUrl: data.portalUrl || prev?.portalUrl || "",
}));
      } else {
        setBooking((prev) => ({ ...prev, items: updatedItems }));
      }

      setStatusMessage("Tjenesten ble slettet.");
    } catch (e) {
      console.error("Feil ved sletting av tjeneste:", e);
      alert("Kunne ikke slette tjenesten.");
    } finally {
      setUpdating(false);
    }
  };

const removeProductFromBooking = async (productIndex) => {
  if (!booking?.items?.[productIndex]) return;

  const product = booking.items[productIndex];
  const productName =
    product?.produktmerke || product?.produktnavn || `produkt ${productIndex + 1}`;

  const remaining = remainingItemIndexes || [];

  const isLastRemaining = remaining.length <= 1;
  const willCancelWholeBooking = isLastRemaining || remaining.length === 1;

  const ok = window.confirm(
    willCancelWholeBooking
      ? `Dette er siste gjenværende service. Vil du kansellere hele bookingen?`
      : `Vil du fjerne "${productName}" fra bookingen?`
  );

  if (!ok) return;

  await cancelItems([productIndex]);
};

const openEditProductModal = (productIndex) => {
  const product = booking?.items?.[productIndex];
  if (!product) return;

  setEditingProductIndex(productIndex);
  setEditProduktmerke(product.produktmerke || product.produktnavn || "");
  setEditFarge(product.farge || "");
  setEditKommentar(product.kommentar || "");
  setEditLeveringsDato(toDateInputValue(product.leveringsDato));
  setEditForventetFerdigDato(toDateInputValue(product.forventetFerdigDato));
  setEditOnsketFerdigDato(toDateInputValue(product.onsketFerdigDato));
  setEditVerkstedSlug(verkstedSlugForProduct(product));

  setEditProductModalOpen(true);
};

  const closeEditProductModal = () => {
    setEditProductModalOpen(false);
    setEditingProductIndex(null);
    setEditProduktmerke("");
    setEditFarge("");
    setEditKommentar("");
    setEditLeveringsDato("");
    setEditForventetFerdigDato("");
    setEditOnsketFerdigDato("");
    setEditVerkstedSlug("");
  };

const saveProductChanges = async () => {
  if (editingProductIndex == null || !booking?.items?.[editingProductIndex]) return;

  setUpdating(true);
  setNotifyMsg("");
  setStatusMessage("");

  try {
    const originalProduct = booking.items[editingProductIndex];

    const oldLeveringsDato = toDateInputValue(originalProduct?.leveringsDato);
    const oldFerdigDato = toDateInputValue(originalProduct?.forventetFerdigDato);

    const newLeveringsDato = editLeveringsDato || "";
    const newFerdigDato = editForventetFerdigDato || "";

    const leveringsDatoChanged = !sameDateValue(oldLeveringsDato, newLeveringsDato);
    const ferdigDatoChanged = !sameDateValue(oldFerdigDato, newFerdigDato);

    const oldOnsketFerdigDato = toDateInputValue(originalProduct?.onsketFerdigDato);
    const newOnsketFerdigDato = editOnsketFerdigDato || "";
    const onsketFerdigDatoChanged =
      !!originalProduct?.ferdigDatoValgfri &&
      !sameDateValue(oldOnsketFerdigDato, newOnsketFerdigDato);

    const anyDateChanged = leveringsDatoChanged || ferdigDatoChanged || onsketFerdigDatoChanged;

    // Flytting til annet verksted — bare før produktet er mottatt. Etter mottak
    // finnes det en egen verkstedservice som ikke følger bookingen.
    const itemConverted = (booking.convertedItemIndexes || []).includes(editingProductIndex);
    const oldVerkstedSlug = verkstedSlugForProduct(originalProduct);
    const nyttVerksted = VERKSTED_VALG.find((v) => v.slug === editVerkstedSlug);
    const verkstedChanged =
      !itemConverted && !!nyttVerksted && nyttVerksted.slug !== oldVerkstedSlug;

    const updatedItems = booking.items.map((prod, idx) => {
      if (idx !== editingProductIndex) return prod;

      // Skriv bare felt som faktisk er endret i skjemaet. Uendrede datoer og
      // tekster beholdes nøyaktig som de er lagret (også format), så f.eks.
      // en flytting til annet verksted ikke rører noe annet innhold.
      const next = { ...prod };
      const textChanged = (field, value) =>
        String(prod?.[field] || "").trim() !== value.trim();

      const oldMerke = prod.produktmerke || prod.produktnavn || "";
      if (oldMerke.trim() !== editProduktmerke.trim()) {
        next.produktmerke = editProduktmerke.trim();
      }
      if (textChanged("farge", editFarge)) next.farge = editFarge.trim();
      if (textChanged("kommentar", editKommentar)) next.kommentar = editKommentar.trim();
      if (leveringsDatoChanged) next.leveringsDato = newLeveringsDato;
      if (ferdigDatoChanged) next.forventetFerdigDato = newFerdigDato;
      if (onsketFerdigDatoChanged) next.onsketFerdigDato = newOnsketFerdigDato || null;

      if (verkstedChanged) {
        next.butikk = nyttVerksted.navn;
        next.butikkSlug = nyttVerksted.slug;
        next.verkstedSlug = nyttVerksted.slug;
      }

      return next;
    });

    const r = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: updatedItems }),
    });

    const data = await r.json();

    if (!r.ok) {
      throw new Error(data?.message || "Kunne ikke oppdatere produktet");
    }

const updatedBooking = data?.booking
  ? {
      ...data.booking,
      portalUrl: data.portalUrl || booking?.portalUrl || "",
    }
  : { ...booking, items: updatedItems };

    setBooking(updatedBooking);

    if (anyDateChanged || verkstedChanged) {
      // Behold en tidligere, ikke-sendt flytting av samme produkt hvis denne
      // redigeringen bare endrer dato, så kunden fortsatt får beskjed om den.
      const prev =
        lastDateChangeSummary?.productIndex === editingProductIndex
          ? lastDateChangeSummary
          : null;
      const keepPrevMove = !verkstedChanged && !!prev?.verkstedChanged;

      setDateChangePending(true);
      setLastDateChangeSummary({
        productIndex: editingProductIndex,
        productName:
          updatedBooking?.items?.[editingProductIndex]?.produktmerke ||
          updatedBooking?.items?.[editingProductIndex]?.produktnavn ||
          "Produkt",
        oldLeveringsDato,
        newLeveringsDato,
        oldFerdigDato,
        newFerdigDato,
        leveringsDatoChanged,
        ferdigDatoChanged,
        oldOnsketFerdigDato,
        newOnsketFerdigDato,
        onsketFerdigDatoChanged,
        verkstedChanged: verkstedChanged || keepPrevMove,
        oldVerkstedNavn: keepPrevMove
          ? prev.oldVerkstedNavn
          : verkstedNavnForProduct(originalProduct),
        newVerkstedNavn: keepPrevMove ? prev.newVerkstedNavn : nyttVerksted?.navn || "",
        newVerkstedSlug: keepPrevMove ? prev.newVerkstedSlug : nyttVerksted?.slug || "",
      });

      setStatusMessage(
        verkstedChanged
          ? `Produktet er flyttet til ${nyttVerksted.navn}. Du kan nå sende oppdatert bookinginfo til kunden.`
          : "Dato ble oppdatert. Du kan nå sende oppdatert bookinginfo til kunden."
      );
    } else {
      setStatusMessage("Produktet ble oppdatert.");
    }

    closeEditProductModal();
  } catch (e) {
    console.error("Feil ved oppdatering av produkt:", e);
    alert("Kunne ikke oppdatere produktet.");
  } finally {
    setUpdating(false);
  }
};

const sendBookingMessage = async () => {
  const text = String(bookingMessageText || "").trim();

  if (!text) {
    setBookingMessageError("Skriv en melding først.");
    return;
  }

  if (!statusActor?.name) {
    setBookingMessageError("Velg ansvarlig ansatt før du sender melding.");
    return;
  }

  setBookingMessageBusy(true);
  setBookingMessageError("");
  setNotifyMsg("");

  try {
    const r = await fetch(`${API_BASE_URL}/bookings/${id}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        fromName: getFirstName(statusActor.name),
      }),
    });

    const data = await r.json().catch(() => ({}));

    if (!r.ok) {
      throw new Error(data?.message || "Kunne ikke sende melding");
    }

    setBooking((prev) => ({
      ...prev,
      messages: Array.isArray(data?.messages) ? data.messages : prev?.messages || [],
    }));

    setBookingMessageText("");

    if (!customerPhone) {
      console.warn("[sendBookingMessage] Ingen telefonnummer på booking – skipper SMS-varsling.");
      setNotifyMsg("Melding sendt til kundeportalen.");
      return;
    }

    if (!portalUrl) {
      console.warn("[sendBookingMessage] Ingen kundelenke på booking – skipper SMS-varsling.");
      setNotifyMsg("Melding sendt til kundeportalen.");
      return;
    }

    await notifyCustomerNewDialogMessage({
      svc: booking,
      text,
      customerName,
      customerPhone,
      customerLink: portalUrl,
      produkt: booking?.items?.[0] || {},
    });

    setNotifyMsg("Melding sendt til kundeportalen og SMS-varsling sendt.");
  } catch (e) {
    console.error("Feil ved sending av bookingmelding:", e);
    setBookingMessageError(e.message || "Kunne ikke sende melding.");
  } finally {
    setBookingMessageBusy(false);
  }
};

  const updateStatus = async (status) => {
    setUpdating(true);
    setStatusMessage("");
    setCreatedServices([]);
    setNotifyMsg("");

    if (!booking) {
      console.warn("Ingen booking lastet ennå, avbryter statusoppdatering.");
      setUpdating(false);
      return;
    }

    // Felles actor-objekt vi kan bruke til lastStatusBy
    const actorPayload = statusActor
      ? {
        id: statusActor.id,
        name: statusActor.name,
        store: Cookies.get("selectedStore") || null,
      }
      : null;


 

    // 👇 Bygg MINIMAL payload som matcher backend
    const payload = {
      status,
      statusComment: statusComment || "",
    };

    if (originalCustomerId) {
      payload.originalCustomerId = originalCustomerId;
    }

    if (status === "mottatt" && selectedItemIndexes.length > 0) {
      payload.selectedItemIndexes = selectedItemIndexes;
    }

    // kun ta med lastStatusBy dersom vi har ansatt
    if (actorPayload && (actorPayload.id || actorPayload.name)) {
      payload.lastStatusBy = actorPayload;
    }

    const url = `${API_BASE_URL}/bookings/${id}`;
    console.groupCollapsed("[BookingDetail] PATCH /bookings/:id");
    console.log("URL:", url);
    console.log("Payload:", payload);

    try {
      const r = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rawText = await r.text();
      console.log("Raw response text:", rawText);

      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch (parseErr) {
        console.error("Kunne ikke parse JSON:", parseErr);
      }

      if (!r.ok) {
        console.error("PATCH feilet. HTTP", r.status, r.statusText, data);
        alert(
          `Feil ved oppdatering av status (HTTP ${r.status}${data?.message ? `: ${data.message}` : ""
          })`
        );
        return;
      }



      // ✅ Oppdatér lokal state og husk convertedToServicesAt
      if (data?.booking) {
        console.log("✅ PATCH JSON:", data);
setBooking((prev) => ({
  ...data.booking,
  portalUrl: data.portalUrl || prev?.portalUrl || "",
}));
        setSelectedItemIndexes([]);
        const afterConverted = data.booking.convertedToServicesAt ?? null;
        console.log(
          "convertedToServicesAt før:",
          prevConvertedRef.current,
          "etter:",
          afterConverted
        );
        prevConvertedRef.current = afterConverted;
      } else {
        console.warn("Ingen 'booking' i responsen.");
      }

      // Sjekk om backend opprettet services
      const created = Array.isArray(data?.createdServices) ? data.createdServices : [];
      if (created.length) {
        setCreatedServices(created);
        setStatusMessage(`Opprettet ${created.length} service-ordre fra denne bookingen.`);
        const ids = created.map((s) => s._id || s.id).filter(Boolean);
        if (ids.length) {
          Promise.all(ids.map((sid) => setQuoteEnabled(sid, false))).catch(() => { });
        }
      } else {
        setStatusMessage("Status oppdatert.");
      }
      if (status === "mottatt") {
        await fetchCreatedServicesForBooking(id);
      }
      // ---- VENT PÅ PORTAL-LENKER OG SEND VARSLER VED 'mottatt' ----
      const preferredIds = created.map((s) => s._id || s.id).filter(Boolean);
      const svcItems = await waitForServiceLinks({
        bookingId: id,
        preferIds: preferredIds,
        tries: 8,
        delayMs: 400,
      }); // -> [{ id, url, service }]

      if (status === "mottatt") {
        const freshBooking = data?.booking || booking;
        const cust = medKontakt(freshBooking?.customer || booking?.customer || {});
        const doEmail = !!sendEmailOnReceived && !!cust.email;
        const doSms = !!sendSmsOnReceived && !!cust.phoneNumber;

        if (doEmail || doSms) {
          setNotifyBusy(true);
          try {
            let anySent = false;

            for (const svc of svcItems) {
              const itemUrl = svc?.url;
              const svcObj = svc?.service;
              if (!itemUrl || !svcObj) continue;

              if (doSms) {
                // Har kunden godtatt servicebetingelsene (online-booking)? Ved
                // feil antas ja, så SMS-en aldri stopper.
                let vilkarGodtatt = true;
                try {
                  const vr = await fetch(`${API_BASE_URL}/samtykker/service/${svc.id}`);
                  if (vr.ok) {
                    const vd = await vr.json();
                    vilkarGodtatt = (vd?.historikk || []).some((x) => x.type === "servicebetingelser");
                  }
                } catch {}
                const ok = await sendSms({
                  phone: cust.phoneNumber,
                  message: buildServiceSms({
                    booking: freshBooking,
                    service: svcObj,
                    url: itemUrl,
                    status,
                    comment: statusComment,
                    vilkarGodtatt,
                  }),
                });
                anySent = anySent || ok;
              }

              if (doEmail) {
                const shortRef = pickShortRef(svcObj, freshBooking?._id);
                const ok = await sendStatusEmail({
                  to: cust.email,
                  customerName: `${cust.firstName || ""} ${cust.lastName || ""
                    }`.trim(),
                  plainText: buildServiceEmail({
                    booking: freshBooking,
                    service: svcObj,
                    url: itemUrl,
                    status,
                  }),
                  storeLocation: getStoreFromBooking(freshBooking),
                  firstUrl: itemUrl,
                  status,
                  serviceRef: shortRef,
                  includeStatusIntro: false,
                  includeRefLine: false,
                });
                anySent = anySent || ok;
              }
            }

            setNotifyMsg(
              anySent ? "Meldinger sendt per service." : "Klarte ikke å sende meldinger."
            );
          } finally {
            setNotifyBusy(false);
          }
        }
      }

      // (valgfritt) liten sanity-logg på workshop-services
      try {
        const listUrl = `${API_BASE_URL}/workshop-services?bookingId=${id}`;
        console.log("[BookingDetail] GET", listUrl, "(for å bekrefte ny collection)");
        const ls = await fetch(listUrl);
        const arr = await ls.json();
        console.log(
          "[BookingDetail] /workshop-services count:",
          Array.isArray(arr) ? arr.length : "ukjent"
        );
        if (Array.isArray(arr) && arr.length) {
          console.log("[BookingDetail] Første 3 services:", arr.slice(0, 3));
        }
      } catch (listErr) {
        console.error("[BookingDetail] Klarte ikke lese /workshop-services:", listErr);
      }
    } catch (e) {
      console.error("PATCH exception:", e);
      alert("Feil ved oppdatering av status");
    } finally {
      console.groupEnd();
      setUpdating(false);
    }
  };

  const cancelItems = async (indexes) => {
    setUpdating(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bookings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cancelItemIndexes: indexes }),
      });
      const data = await r.json();
      if (!r.ok) {
        alert(`Feil ved kansellering (HTTP ${r.status}${data?.message ? `: ${data.message}` : ""})`);
        return;
      }
      if (data?.booking) {
        setBooking((prev) => ({ ...data.booking, portalUrl: prev?.portalUrl || "" }));
      }
      setSelectedCancelIndexes([]);
      setShowCancelModal(false);
    } catch {
      alert("Feil ved kansellering");
    } finally {
      setUpdating(false);
    }
  };

  const mottaker = medKontakt(booking?.customer || {});
  const emailValue = String(mottaker.email || "").trim();
  const hasEmail = !!emailValue;

  const phoneValue = String(mottaker.phoneNumber || "").trim();
  const hasPhone = !!phoneValue;

  const customerPhone = normalizePhone(mottaker.phoneNumber || "");
const customerName =
  `${booking?.customer?.firstName || ""} ${booking?.customer?.lastName || ""}`.trim() || "Kunde";


const CUSTOMER_PORTAL_BASE = "https://status.roykensykkelservice.no";

const getFirstName = (name = "") => {
  return String(name || "").trim().split(/\s+/)[0] || "";
};

const bookingMessages = Array.isArray(booking?.messages)
  ? [...booking.messages].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    )
  : [];


const toggleBookingDialog = () => {
  setBookingDialogOpen((prev) => {
    const next = !prev;

    if (!prev && next) {
      markAllBookingCustomerMessagesAsRead();
    }

    return next;
  });
};

const portalUrl =
  booking?.portal?.token
    ? `${CUSTOMER_PORTAL_BASE}/bookingportal/${booking.portal.token}`
    : "";

const deliveryReminderSmsPreview = buildDeliveryReminderSms({
  booking,
  portalUrl,
});

const deliveryReminderEmailPreview = buildDeliveryReminderEmailPreview({
  booking,
  portalUrl,
});




if (!booking) return <div className="p-6">Laster…</div>;

const isAlreadyReceived = String(booking.status || "").toLowerCase() === "mottatt";
const isCancelled = String(booking.status || "").toLowerCase() === "kansellert";

const remainingItemIndexes = (booking.items || [])
  .map((_, i) => i)
  .filter(i => !(booking.convertedItemIndexes || []).includes(i));

const hasRemainingItems = remainingItemIndexes.length > 0;

  /* ---------- totals ---------- */
  const bookingTotal = Array.isArray(booking.items)
    ? booking.items.reduce((sum, prod) => {
      const rows = extractServiceRows(prod);
      return sum + rows.reduce((s, r) => s + (getPrice(r) ?? 0), 0);
    }, 0)
    : 0;

  if (Array.isArray(booking.items)) {
    debugSumProducts(booking.items);
  }

  // --- Label printing (90x29mm, landscape) ---
  const printLabel = ({ name, dateStr, reference, service }) => {
    const safeName = escapeHtml(name || "Kunde");
    const safeDate = escapeHtml(dateStr || new Date().toLocaleDateString("no-NO"));
    const safeRef = escapeHtml(reference || pickShortRef(service, booking?._id) || "—");

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Label</title>
<style>
  /* Viktig: ingen ytre marger i print */
  @page {
    size: 90mm 29mm;
    margin: 0;
  }

  html, body {
    width: 90mm;
    height: 29mm;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Label med innvendige marger */
  .label {
    width: 100%;
    height: 100%;
    padding: 2mm 3mm;       /* 👈 2mm topp/bunn + 3mm venstre/høyre */
    box-sizing: border-box;

    display: grid;
    grid-template-rows: auto auto auto;
    row-gap: 1.8mm;

    overflow: hidden;
  }

  .name {
    font-weight: 700;
    font-size: 12pt;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .row {
    display: flex;
    justify-content: space-between;
    font-size: 9pt;
  }

  .muted {
    opacity: 0.85;
  }

  .ref {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
    font-size: 11pt;
    font-weight: 700;
  }
</style>
</head>
<body>
  <div class="label">
    <div class="name">${safeName}</div>
    <div class="row muted"><span>Mottatt</span><span>${safeDate}</span></div>
    <div class="row"><span>Ref</span><span class="ref">${safeRef}</span></div>
  </div>

  <script>
    window.onload = () => {
      window.print();
      setTimeout(() => window.close(), 300);
    };
  </script>
</body>
</html>`;

    const w = window.open("", "print_label", "width=600,height=300");
    if (!w) return alert("Popup ble blokkert. Tillat popups for å skrive ut etikett.");
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

const activeDeliveryReminder = booking?.deliveryReminder || null;

const hasActiveDeliveryReminder =
  !!activeDeliveryReminder?.enabled &&
  !!activeDeliveryReminder?.sendAt;

return (
  <div className="mx-auto w-full max-w-7xl pb-10">
    {/* Tilbake og sidetopp i det nye designet (vinnsel, 30.10). Tilbake går som før til forrige side. */}
    <div className="mb-4 space-y-3">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50"
      >
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake
      </button>
      <Sidetopp
        ikon="kalender"
        farge="verksted"
        tittel="Booking"
        undertittel={`Opprettet: ${new Date(booking.createdAt).toLocaleString("no-NO")}`}
      />
    </div>

    <SmsTilKunden telefon={phoneValue} />

    {/* Mottak / handling */}
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b bg-slate-50 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">Registrer innlevering</h3>
            <p className="mt-0.5 text-sm text-slate-600">
              Bekreft kunde og produkter, og registrer mottak.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700">
              Status: {booking.status || "—"}
            </span>
            {booking.items?.[0]?.bookingtype && (
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-800">
                📅 {bookingTypeLabel(booking.items[0].bookingtype)}
              </span>
            )}
            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${booking.createdBy ? "border-indigo-200 bg-indigo-50 text-indigo-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              {booking.createdBy?.name
                ? `👤 ${booking.createdBy.name}`
                : booking.createdBy
                  ? "🔧 Mekaniker"
                  : "🌐 Kunde selv"}
            </span>
          </div>
        </div>
      </div>

      <div className="p-3">
        {!isAlreadyReceived && !isCancelled && (
          <div className="mb-4 rounded-2xl border border-green-300 bg-green-50 p-3">
            <div className="mb-3 text-sm font-semibold text-green-900">Utfør mottak her</div>

            <div className="mb-4 grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Intern kommentar
                </label>
                <input
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5"
                  placeholder="Tilleggsinformasjon ved mottak …"
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  maxLength={1000}
                />
              </div>
            </div>

            <p className="mb-2 text-xs text-slate-700">
              Bekreft telefonnummeret med kunden før du tar imot sykkelen. Servicen kobles til kundekortet for dette nummeret.
            </p>
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border border-slate-200 bg-white px-3 py-2">
                <input
                  type="checkbox"
                  checked={sendSmsOnReceived}
                  onChange={(e) => setSendSmsOnReceived(e.target.checked)}
                />
                <span className="whitespace-nowrap">Send SMS</span>
                <span className="ml-auto min-w-0 break-all text-xs text-slate-600">
                  {hasPhone ? phoneValue : "Mangler telefonnummer"}
                </span>
              </label>

              <label
                className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border border-slate-200 bg-white px-3 py-2 ${
                  !hasEmail ? "opacity-70" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={sendEmailOnReceived}
                  disabled={!hasEmail}
                  onChange={(e) => setSendEmailOnReceived(e.target.checked)}
                  title={!hasEmail ? "Låst: kunden mangler e-postadresse" : ""}
                />
                <span className="whitespace-nowrap">Send e-post</span>
                <span className="ml-auto min-w-0 break-all text-xs text-slate-600">
                  {hasEmail ? emailValue : "Mangler e-postadresse"}
                </span>
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {isAlreadyReceived && createdServices.length > 0 ? (
                createdServices.map((s) => (
                  <button
                    key={s._id || s.id}
                    onClick={() => navigate(`/workshop/${s._id || s.id}`)}
                    className="rounded-2xl px-6 py-3 text-base font-semibold text-white shadow bg-indigo-600 hover:bg-indigo-700"
                  >
                    {createdServices.length > 1
                      ? `Gå til service: ${s.produktnavn || s.title || s.name || "—"}`
                      : "Gå til service"}
                  </button>
                ))
              ) : (
                <button
disabled={
  !hasRemainingItems ||
  isAlreadyReceived ||
  updating ||
  notifyBusy ||
  emailBusy ||
  smsBusy
}
onClick={() => {
  if (!hasRemainingItems || isAlreadyReceived) return;
  setShowEmployeeConfirmModal(true);
}}
className={`rounded-2xl px-6 py-3 text-base font-semibold text-white shadow ${
  !hasRemainingItems ||
  isAlreadyReceived ||
  updating ||
  notifyBusy ||
  emailBusy ||
  smsBusy
    ? "cursor-not-allowed bg-green-400"
    : "bg-green-600 hover:bg-green-700"
}`}
title={
  !hasRemainingItems
    ? "Alle servicer er allerede mottatt"
    : isAlreadyReceived
    ? "Allerede registrert som mottatt"
    : "Registrer som mottatt"
}
                  aria-busy={updating || notifyBusy || emailBusy || smsBusy ? "true" : "false"}
                >
{!hasRemainingItems
  ? "Alle servicer er mottatt"
  : remainingItemIndexes.length === 1 || selectedItemIndexes.length > 0
  ? `Registrer som mottatt (${selectedItemIndexes.length > 0 ? selectedItemIndexes.length : 1})`
  : "Registrer som mottatt"}
                </button>
              )}

<button
  type="button"
  className={`rounded-2xl border px-4 py-3 text-sm ${
    !hasRemainingItems || updating
      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500"
      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
  }`}
  onClick={() => {
    if (!hasRemainingItems || updating) return;

    if (remainingItemIndexes.length > 1 && selectedCancelIndexes.length === 0) {
      setShowCancelModal(true);
      return;
    }

    const toCancel =
      selectedCancelIndexes.length > 0
        ? selectedCancelIndexes
        : remainingItemIndexes;

    if (toCancel.length >= remainingItemIndexes.length) {
      if (window.confirm("Vil du kansellere hele bookingen?")) {
        cancelItems(toCancel);
      }
    } else {
      cancelItems(toCancel);
    }
  }}
  disabled={!hasRemainingItems || updating}
  title={!hasRemainingItems ? "Alle servicer er mottatt og kan ikke kanselleres her" : ""}
>
  {!hasRemainingItems
    ? "Kan ikke kansellere"
    : selectedCancelIndexes.length > 0
    ? selectedCancelIndexes.length >= remainingItemIndexes.length
      ? "Kanseller booking"
      : `Fjern (${selectedCancelIndexes.length})`
    : remainingItemIndexes.length > 1
    ? "Fjern service"
    : "Kanseller booking"}
</button>
            </div>
          </div>
        )}

        {showEmployeeConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              <h2 className="mb-1 text-lg font-semibold text-slate-900">Bekreft ansvarlig ansatt</h2>
              <p className="mb-4 text-sm text-slate-600">Sjekk at riktig ansatt er valgt før servicen registreres som mottatt.</p>

              <label className="mb-1 block text-sm font-medium text-slate-700">Ansvarlig ansatt</label>
              <select
                key={storeName}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 mb-4"
                value={selectedEmployeeName}
                onChange={(e) => {
                  const name = e.target.value;
                  setSelectedEmployeeName(name);
                  const low = (s) => String(s || "").trim().toLowerCase();
                  const emp =
                    employeesForStore.find((x) => x.name === name) ||
                    employeesForStore.find((x) => low(x.name) === low(name)) ||
                    employeesForStore.find((x) => low(x.name).includes(low(name))) ||
                    null;
                  if (emp) {
                    setActiveEmployee(emp, "manual-change");
                    persistEmployeeSelection(emp);
                  } else {
                    setStatusActor(null);
                  }
                }}
              >
                {!employeesForStore.length && selectedEmployeeName ? (
                  <option value={selectedEmployeeName}>{selectedEmployeeName} (fra navbar)</option>
                ) : null}
                <option value="">
                  {employeesForStore.length ? "Velg ansvarlig ansatt …" : "Laster ansatte …"}
                </option>
                {employeesForStore.map((emp) => (
                  <option key={emp.id} value={emp.name}>{emp.name}</option>
                ))}
              </select>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowEmployeeConfirmModal(false)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Avbryt
                </button>
                <button
                  disabled={!statusActor?.id}
                  onClick={() => {
                    setShowEmployeeConfirmModal(false);
                    if (remainingItemIndexes.length > 1 && selectedItemIndexes.length === 0) {
                      setShowReceiveModal(true);
                      return;
                    }
                    updateStatus("mottatt");
                  }}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${
                    !statusActor?.id
                      ? "cursor-not-allowed bg-green-400"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {statusActor?.name ? `Bekreft — ${statusActor.name}` : "Velg ansatt først"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showReceiveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h2 className="mb-1 text-lg font-semibold text-slate-900">Velg produkter som leveres inn nå</h2>
              <p className="mb-4 text-sm text-slate-600">Andre produkter kan registreres senere.</p>

              <div className="mb-6 space-y-3">
                {(booking.items || []).map((prod, idx) => {
                  if ((booking.convertedItemIndexes || []).includes(idx)) return null;
                  return (
                    <label key={idx} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={selectedItemIndexes.includes(idx)}
                        onChange={() => toggleItemSelection(idx)}
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">
                          {prod.produktmerke || prod.produktnavn || "Produkt"}
                          {prod.farge ? ` (${prod.farge})` : ""}
                        </div>
                        {prod.leveringsDato && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Booket tid til service: {formatDateNo(prod.leveringsDato)}
                          </div>
                        )}
                        {prod.forventetFerdigDato && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Estimert ferdig: {formatDateNo(prod.forventetFerdigDato)}
                          </div>
                        )}
                        {prod.ferdigDatoValgfri && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Ønsket ferdig: {onsketFerdigTekst(prod, formatDateNo)}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowReceiveModal(false); setSelectedItemIndexes([]); }}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Avbryt
                </button>
                <button
                  disabled={selectedItemIndexes.length === 0}
                  onClick={() => { setShowReceiveModal(false); updateStatus("mottatt"); }}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${
                    selectedItemIndexes.length === 0
                      ? "cursor-not-allowed bg-green-400"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {selectedItemIndexes.length === 0
                    ? "Velg minst ett produkt"
                    : `Registrer ${selectedItemIndexes.length} av ${(booking.items || []).filter((_, i) => !(booking.convertedItemIndexes || []).includes(i)).length} gjenstående`}
                </button>
              </div>
            </div>
          </div>
        )}

{showCancelModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <h2 className="mb-1 text-lg font-semibold text-slate-900">
        Velg hva som skal fjernes
      </h2>

      <p className="mb-4 text-sm text-slate-600">
        Velg én eller flere servicer som skal fjernes fra bookingen. Velger du alle,
        kanselleres hele bookingen.
      </p>

      <div className="mb-6 space-y-3">
        {(booking.items || []).map((prod, idx) => {
          if ((booking.convertedItemIndexes || []).includes(idx)) return null;

          return (
            <label
              key={idx}
              className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selectedCancelIndexes.includes(idx)}
                onChange={() => toggleCancelSelection(idx)}
                className="h-4 w-4 accent-red-600"
              />

              <div className="flex-1">
                <div className="font-medium text-slate-900">
                  {prod.produktmerke || prod.produktnavn || "Produkt"}
                  {prod.farge ? ` (${prod.farge})` : ""}
                </div>

                {prod.leveringsDato && (
                  <div className="mt-0.5 text-xs text-slate-600">
                    Booket tid til service: {formatDateNo(prod.leveringsDato)}
                  </div>
                )}

                {prod.forventetFerdigDato && (
                  <div className="mt-0.5 text-xs text-slate-600">
                    Estimert ferdig: {formatDateNo(prod.forventetFerdigDato)}
                  </div>
                )}

                {prod.ferdigDatoValgfri && (

                  <div className="mt-0.5 text-xs text-slate-600">

                    Ønsket ferdig: {onsketFerdigTekst(prod, formatDateNo)}

                  </div>

                )}
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => {
            setShowCancelModal(false);
            setSelectedCancelIndexes([]);
          }}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Avbryt
        </button>

        <button
          disabled={selectedCancelIndexes.length === 0}
          onClick={() => cancelItems(selectedCancelIndexes)}
          className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${
            selectedCancelIndexes.length === 0
              ? "cursor-not-allowed bg-red-300"
              : "bg-red-600 hover:bg-red-700"
          }`}
        >
          {selectedCancelIndexes.length === 0
            ? "Velg minst én service"
            : selectedCancelIndexes.length >= remainingItemIndexes.length
            ? "Kanseller hele bookingen"
            : `Fjern ${selectedCancelIndexes.length} av ${remainingItemIndexes.length}`}
        </button>
      </div>
    </div>
  </div>
)}

        {isAlreadyReceived && (
          <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
            Bookingen er registrert som mottatt.
          </div>
        )}

        {isCancelled && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            Bookingen er kansellert. Ingen endringer kan gjøres.
          </div>
        )}

        {/* Kunden: felles banner med lenke til kundekortet (KundeBanner, vinnsel 30.10). */}
        <KundeBanner
          className="mb-3"
          kundeId={booking.originalCustomerId}
          navn={`${booking.customer?.firstName || ""} ${booking.customer?.lastName || ""}`.trim()}
          telefon={booking.customer?.phoneNumber}
          epost={booking.customer?.email}
        >
          {booking.status === "booking" && !rettTlfApen && (
            <button
              type="button"
              onClick={() => { setRettTlf(booking.customer?.phoneNumber || ""); setRettTlfMsg(""); setRettTlfApen(true); }}
              className="rounded-lg font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            >
              Rett telefonnummeret
            </button>
          )}
          {rettTlfApen && (
            <form onSubmit={lagreRettTelefon} className="mt-2 flex flex-wrap items-center gap-2">
              <label htmlFor="rett-telefon-ansatt" className="sr-only">Riktig telefonnummer</label>
              <input
                id="rett-telefon-ansatt"
                type="tel"
                autoFocus
                value={rettTlf}
                onChange={(e) => setRettTlf(e.target.value)}
                className="w-36 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              />
              <label className="flex items-center gap-1 text-xs text-slate-700">
                <input type="checkbox" checked={rettTlfSend} onChange={(e) => setRettTlfSend(e.target.checked)} />
                Send bekreftelsen til nytt nummer
              </label>
              <button type="submit" disabled={rettTlfBusy} className="rounded-lg bg-indigo-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">
                {rettTlfBusy ? "Lagrer…" : "Lagre"}
              </button>
              <button type="button" onClick={() => setRettTlfApen(false)} className="rounded-lg border px-2 py-1 text-xs">Avbryt</button>
            </form>
          )}
          {rettTlfMsg && <div className="mt-1 text-xs text-slate-700" role="status">{rettTlfMsg}</div>}
          {kontakt?.egenEpostPaSaken && (
            <div className="text-xs text-slate-700">
              Kunden har valgt en annen e-post for denne bookingen enn den på kundekortet. Meldinger om
              bookingen går til e-posten på bookingen.
            </div>
          )}
          {kontakt?.fraKundekort && (kontakt.vedRegistrering?.telefon || kontakt.vedRegistrering?.epost) && (
            <div className="text-xs text-slate-700">
              Meldinger går til kundekortet: {[kontakt.telefon, kontakt.epost].filter(Boolean).join(", ")}
            </div>
          )}
        </KundeBanner>

        {/* Kompakt oppsummering */}
        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-600">
                  Prisoverslag
                </div>
                <div className="text-sm text-slate-800">
                  {booking.customer?.prisoverslag ? "Ja" : "Nei"}
                </div>
              </div>
            </div>

            {Array.isArray(booking.items) && booking.items.length > 0 && (
              <div>
                <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-600">
                  Produkter som leveres inn
                </div>

                <div className="flex flex-wrap gap-2">
                  {booking.items.map((prod, idx) => {
                    const serviceRows = extractServiceRowsWithMeta(prod);
                    const itemConverted = (booking.convertedItemIndexes || []).includes(idx);
                    const linkedService = (isAlreadyReceived || itemConverted)
                      ? findCreatedServiceForProduct(createdServices, prod, idx)
                      : null;
                    const serviceId = linkedService?._id || linkedService?.id;
const linkedServiceRef = linkedService
  ? pickShortRef(linkedService, linkedService?._id || booking?._id)
  : "";

const isSelected = selectedItemIndexes.includes(idx);

                    const isSelectable = !isAlreadyReceived && !itemConverted && (booking.items || []).length > 1;
                    const isAutoSelected = isSelectable && remainingItemIndexes.length === 1;

                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          if (serviceId) { navigate(`/workshop/${serviceId}`); return; }
                          if (isSelectable && !isAutoSelected) toggleItemSelection(idx);
                        }}
                        className={`rounded-xl border px-2.5 py-1.5 text-sm transition-colors ${
                          serviceId
                            ? "cursor-pointer border-green-400 bg-green-50 hover:border-green-500 hover:bg-green-100"
                            : isAutoSelected
                            ? "border-indigo-500 bg-indigo-50"
                            : isSelectable
                            ? isSelected
                              ? "cursor-pointer border-indigo-500 bg-indigo-50"
                              : "cursor-pointer border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
                            : "border-slate-200 bg-white"
                        }`}
                        title={serviceId ? "Gå til service" : isAutoSelected ? "Registreres automatisk" : isSelectable ? "Klikk for å velge" : undefined}
                      >
{serviceId && (
  <div className="mb-1 flex flex-wrap items-center gap-1 text-xs font-semibold text-green-700">
    <span>✓</span>
    <span>Mottatt</span>
    {linkedServiceRef && (
      <span className="ml-1 font-mono text-green-800">
        Ref: {linkedServiceRef}
      </span>
    )}
  </div>
)}

                        <div className="flex items-center gap-1.5">
                          {isSelectable && (
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-lg border text-xs ${
                              isSelected || isAutoSelected ? "border-indigo-500 bg-indigo-500 text-white" : "border-slate-300"
                            }`}>
                              {(isSelected || isAutoSelected) && "✓"}
                            </span>
                          )}
                          <span className="font-medium leading-tight text-slate-900">
                            {prod.produktmerke || prod.produktnavn || "Produkt"}
                            {prod.farge ? ` (${prod.farge})` : ""}
                          </span>
                        </div>

                        {serviceRows.length > 0 && (
                          <div className="mt-0.5 text-xs leading-tight text-slate-600">
                            {serviceRows
                              .map((entry) => entry.row?.produktnavn || entry.row?.title || "Tjeneste")
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        )}

                        {prod.leveringsDato && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Booket tid til service: {formatDateNo(prod.leveringsDato)}
                          </div>
                        )}
                        {prod.forventetFerdigDato && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Estimert ferdig: {formatDateNo(prod.forventetFerdigDato)}
                          </div>
                        )}
                        {prod.ferdigDatoValgfri && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            Ønsket ferdig: {onsketFerdigTekst(prod, formatDateNo)}
                          </div>
                        )}

                        {serviceId && (
                          <div className="mt-1 text-xs font-medium text-indigo-700">
                            Åpne service →
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {statusMessage ? (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
            {statusMessage}
          </div>
        ) : null}

        {notifyMsg ? (
          <div
            className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900"
            aria-live="polite"
          >
            {notifyMsg}
          </div>
        ) : null}

        {(createdServices.length > 0 || isAlreadyReceived || (booking.convertedItemIndexes || []).length > 0) ? (
          <div className="mt-4 rounded-lg border bg-slate-50 p-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Skriv ut etikett på mottatt service</h4>
              {createdServices.length > 0 && <div className="flex gap-2" />}
            </div>

            {createdServices.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-3">
                {createdServices.map((s) => {
                  const serviceTitle =
                    s?.produkt?.produktmerke ||
                    s?.service?.produktnavn ||
                    s?.service?.title ||
                    s?.service?.navn ||
                    "Tjeneste";
                  const ref = pickShortRef(s, booking?._id);
                  const custName = `${booking?.customer?.firstName || ""} ${booking?.customer?.lastName || ""}`.trim() || "Kunde";
                  const dateStr = new Date().toLocaleDateString("no-NO");

                  return (
                    <div key={s._id || s.id}>
                      <div className="mb-1 text-xs text-slate-600">{serviceTitle}</div>
                      <div
                        onClick={() => printLabel({ name: custName, dateStr, reference: ref, service: s })}
                        title="Klikk for å skrive ut etikett"
                        className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 bg-white transition-colors hover:border-slate-500 hover:shadow-sm"
                        style={{ width: 270, height: 87 }}
                      >
                        <div className="flex h-full flex-col justify-between p-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap font-bold" style={{ fontSize: 14 }}>
                            {custName}
                          </div>
                          <div className="flex justify-between text-slate-600" style={{ fontSize: 11 }}>
                            <span>Mottatt</span>
                            <span>{dateStr}</span>
                          </div>
                          <div className="flex justify-between" style={{ fontSize: 13 }}>
                            <span className="text-slate-600">Ref</span>
                            <span className="font-mono font-bold">{ref || "—"}</span>
                          </div>
                          <div className="text-center text-slate-500" style={{ fontSize: 10 }}>
                            Klikk for å skrive ut etikett (90×29mm)
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-2 text-sm text-slate-600">
                Ingen opprettede service-ordre funnet ennå.
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>

    {/* Samleseksjon */}
    <div className="mb-4 overflow-hidden rounded-lg border bg-white">
      <button
        type="button"
        onClick={() => setDetailsSectionOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left hover:bg-slate-50"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-slate-900">Valgte tjenester og detaljer</h3>

          <p className="mt-1 text-sm text-slate-600">
            Klikk for å {detailsSectionOpen ? "skjule" : "vise"} tjenester, oppdateringer, dialog
            og kundeportal.
          </p>
        </div>

        <span className="shrink-0 text-sm font-medium text-indigo-700">
          {detailsSectionOpen ? "Skjul" : "Vis"}
        </span>
      </button>

      {detailsSectionOpen && (
        <div className="space-y-4 border-t p-4">
          <div className="rounded-lg border p-4">
            <h3 className="mb-2 text-base font-bold text-slate-900">Valgte tjenester</h3>

            {Array.isArray(booking.items) && booking.items.length ? (
              <>
                <ul className="ml-5 list-disc space-y-4">
                  {booking.items.map((prod, idx) => {
                    const serviceRows = extractServiceRowsWithMeta(prod);
                    const productSubtotal = serviceRows.reduce(
                      (sum, entry) => sum + (getPrice(entry.row) ?? 0),
                      0
                    );

                    const linkedService = findCreatedServiceForProduct(createdServices, prod, idx);
                    const linkedServiceRef = linkedService
                      ? pickShortRef(linkedService, linkedService?._id)
                      : "";

                    return (
                      <li key={idx}>
                        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                          <div className="min-w-0 font-medium">
                            {prod.produktmerke || prod.produktnavn || "Produkt"}
                            {prod.farge ? ` (${prod.farge})` : ""}{" "}
                            {prod.produkttype ? `– ${prod.produkttype}` : ""}
                          </div>

                          {!isCancelled && (
                            <div className="flex shrink-0 items-center gap-3">
                              <button
                                type="button"
                                className={`text-sm underline underline-offset-4 ${
                                  updating
                                    ? "cursor-not-allowed text-slate-500"
                                    : "text-indigo-700 hover:text-indigo-800"
                                }`}
                                disabled={updating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditProductModal(idx);
                                }}
                                title="Rediger produkt"
                              >
                                Rediger
                              </button>

                              <button
                                type="button"
                                className={`text-sm underline underline-offset-4 ${
                                  booking.items.length <= 1 || updating
                                    ? "cursor-not-allowed text-slate-500"
                                    : "text-red-700 hover:text-red-900"
                                }`}
                                disabled={updating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeProductFromBooking(idx);
                                }}
                                title={
                                  booking.items.length <= 1
                                    ? "Kan ikke slette siste produkt"
                                    : "Slett hele produktet"
                                }
                              >
                                Slett produkt
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="ml-1 mt-1 space-y-1 text-sm text-slate-600">
                          {prod.leveringsDato && (
                            <div>Leveringsdato: {String(prod.leveringsDato).slice(0, 10)}</div>
                          )}

                          {prod.forventetFerdigDato && (
                            <div>
                              Forventet ferdig innen:{" "}
                              {String(prod.forventetFerdigDato).slice(0, 10)}
                            </div>
                          )}

                          {prod.ferdigDatoValgfri && (
                            <div>
                              Ønsket ferdig:{" "}
                              {onsketFerdigTekst(prod, (d) => String(d).slice(0, 10))}
                            </div>
                          )}

                          {linkedServiceRef && (
                            <div>
                              <span className="font-medium">Service-ref:</span>{" "}
                              <span className="font-mono">{linkedServiceRef}</span>
                            </div>
                          )}

                          {prod.kommentar && (
                            <div>
                              <span className="font-medium">Produktkommentar:</span> {prod.kommentar}
                            </div>
                          )}

                          {prod.prioritering?.grunn && (
                            <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-red-700">Fortløpende kø</span>
                                {prod.prioritering.haster && (
                                  <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide">HASTER</span>
                                )}
                              </div>
                              <div className="text-slate-700">
                                <span className="font-medium">Grunn:</span>{" "}
                                {{
                                  etterjustering: "Etterjustering / førstegangsservice",
                                  akuttservice: "Akuttservice",
                                  reklamasjon: "Reklamasjon",
                                  kundeprioritering: "Kundeprioritering",
                                  avtalemekaniker: "Avtale med mekaniker",
                                }[prod.prioritering.grunn] ?? prod.prioritering.grunn}
                              </div>
                              {prod.prioritering.forklaring && (
                                <div className="text-slate-600 italic">"{prod.prioritering.forklaring}"</div>
                              )}
                            </div>
                          )}
                        </div>

                        {serviceRows.length ? (
                          <ul className="ml-5 mt-2 list-[circle] space-y-1">
                            {serviceRows.map((entry, i) => {
                              const t = entry.row;
                              const pris = getPrice(t);
                              const kommentar = getComment(t);
                              const tidsbruk = t.tidsbruk != null ? toNumber(t.tidsbruk) : null;

                              return (
                                <li
                                  key={`${entry.source}-${entry.index}-${i}`}
                                  className="leading-snug"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-baseline gap-x-2">
                                        <span>{t.produktnavn || t.title || "Tjeneste"}</span>
                                        {pris != null && (
                                          <span className="whitespace-nowrap text-sm text-slate-700">
                                            – {formatNOK(pris)}
                                          </span>
                                        )}
                                        {Number.isFinite(tidsbruk) && tidsbruk > 0 && (
                                          <span className="whitespace-nowrap text-xs text-slate-600">
                                            ({tidsbruk} min)
                                          </span>
                                        )}
                                      </div>

                                      {kommentar ? (
                                        <div className="ml-1 text-sm text-slate-600">{kommentar}</div>
                                      ) : null}
                                    </div>

                                    <button
                                      type="button"
                                      className={`shrink-0 text-sm underline underline-offset-4 ${
                                        serviceRows.length <= 1 || updating
                                          ? "cursor-not-allowed text-slate-500"
                                          : "text-red-600 hover:text-red-800"
                                      }`}
                                      disabled={serviceRows.length <= 1 || updating}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeServiceFromBooking({
                                          productIndex: idx,
                                          source: entry.source,
                                          serviceIndex: entry.index,
                                          mirrorIndex: entry.mirrorIndex,
                                        });
                                      }}
                                      title={
                                        serviceRows.length <= 1
                                          ? "Kan ikke slette siste tjeneste"
                                          : "Slett tjeneste"
                                      }
                                    >
                                      Slett
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <div className="ml-5 mt-1 text-slate-600">
                            Ingen tjenester registrert for dette produktet.
                          </div>
                        )}

                        {productSubtotal > 0 ? (
                          <div className="ml-1 mt-2 text-sm text-slate-900">
                            <span className="font-medium">Delsum:</span> {formatNOK(productSubtotal)}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>

                {bookingTotal > 0 ? (
                  <div className="mt-4 border-t pt-3 text-base">
                    <span className="font-semibold">Total:</span> {formatNOK(bookingTotal)}
                  </div>
                ) : null}
              </>
            ) : (
              <p>Ingen tjenester registrert.</p>
            )}
          </div>

          <div
            className={`rounded-lg border p-4 ${
              dateChangePending ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"
            }`}
          >
            <h3 className="mb-2 text-base font-bold text-slate-900">Send oppdatert bookinginfo</h3>

            {!dateChangePending ? (
              <p className="mb-3 text-sm text-slate-600">
                Ingen nye endringer registrert. Denne brukes når leveringsdato, forventet
                ferdig-dato eller verksted er endret.
              </p>
            ) : (
              <div className="mb-3 rounded-xl border border-amber-200 bg-white p-3 text-sm text-amber-900">
                <div className="mb-1 font-semibold">
                  {lastDateChangeSummary?.verkstedChanged
                    ? "Endring registrert"
                    : "Datoendring registrert"}
                </div>

                {lastDateChangeSummary?.productName ? (
                  <div className="mb-2">
                    Produkt: <span className="font-medium">{lastDateChangeSummary.productName}</span>
                  </div>
                ) : null}

                <div className="space-y-1">
                  {lastDateChangeSummary?.verkstedChanged ? (
                    <div>
                      Verksted:
                      <span className="mx-2 text-slate-600 line-through">
                        {lastDateChangeSummary.oldVerkstedNavn}
                      </span>
                      <span className="font-medium text-green-700">
                        {lastDateChangeSummary.newVerkstedNavn}
                      </span>
                    </div>
                  ) : null}

                  {lastDateChangeSummary?.leveringsDatoChanged ? (
                    <div>
                      Innlevering:
                      <span className="mx-2 text-slate-600 line-through">
                        {formatDateNoShort(lastDateChangeSummary.oldLeveringsDato)}
                      </span>
                      <span className="font-medium text-green-700">
                        {formatDateNoShort(lastDateChangeSummary.newLeveringsDato)}
                      </span>
                    </div>
                  ) : null}

                  {lastDateChangeSummary?.onsketFerdigDatoChanged ? (
                    <div>
                      Ønsket ferdig:
                      <span className="mx-2 text-slate-600 line-through">
                        {lastDateChangeSummary.oldOnsketFerdigDato
                          ? formatDateNoShort(lastDateChangeSummary.oldOnsketFerdigDato)
                          : IKKE_SATT}
                      </span>
                      <span className="font-medium text-green-700">
                        {lastDateChangeSummary.newOnsketFerdigDato
                          ? formatDateNoShort(lastDateChangeSummary.newOnsketFerdigDato)
                          : IKKE_SATT}
                      </span>
                    </div>
                  ) : null}

                  {lastDateChangeSummary?.ferdigDatoChanged ? (
                    <div>
                      Forventet ferdig innen:
                      <span className="mx-2 text-slate-600 line-through">
                        {formatDateNoShort(lastDateChangeSummary.oldFerdigDato)}
                      </span>
                      <span className="font-medium text-green-700">
                        {formatDateNoShort(lastDateChangeSummary.newFerdigDato)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <input
                  type="checkbox"
                  checked={sendUpdatedSms}
                  disabled={!hasPhone || updatedNotifyBusy || !dateChangePending}
                  onChange={(e) => setSendUpdatedSms(e.target.checked)}
                />
                <span className="whitespace-nowrap">Send oppdatert SMS</span>
                <span className="min-w-0 break-all text-xs text-slate-600">
                  {hasPhone ? phoneValue : "Mangler telefonnummer"}
                </span>
              </label>

              <label className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${!hasEmail ? "opacity-70" : ""}`}>
                <input
                  type="checkbox"
                  checked={sendUpdatedEmail}
                  disabled={!hasEmail || updatedNotifyBusy || !dateChangePending}
                  onChange={(e) => setSendUpdatedEmail(e.target.checked)}
                />
                <span className="whitespace-nowrap">Send oppdatert e-post</span>
                <span className="min-w-0 break-all text-xs text-slate-600">
                  {hasEmail ? emailValue : "Mangler e-postadresse"}
                </span>
              </label>
            </div>

            <button
              type="button"
              className={`rounded-xl px-4 py-2 text-white ${
                updatedNotifyBusy || !dateChangePending
                  ? "cursor-not-allowed bg-indigo-400"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
              disabled={updatedNotifyBusy || !dateChangePending || (!hasPhone && !hasEmail)}
              onClick={sendUpdatedBookingNotification}
            >
              {updatedNotifyBusy ? "Sender..." : "Send oppdatert bookinginfo"}
            </button>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-teal-200 bg-teal-50">
            <button
              type="button"
              onClick={toggleBookingDialog}
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left bg-teal-100 hover:bg-teal-200 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-teal-700 shrink-0"><path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" /></svg>
                  <h3 className="font-semibold text-teal-900">Dialog med kunde</h3>

                  {bookingMessageCount > 0 && (
                    <span className="inline-flex items-center rounded-full bg-teal-200 px-2 py-0.5 text-xs text-teal-800">
                      {bookingMessageCount} melding{bookingMessageCount === 1 ? "" : "er"} fra kunde
                    </span>
                  )}

                  {!bookingDialogOpen && unreadBookingCustomerCount > 0 && (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                      {unreadBookingCustomerCount} ulest
                    </span>
                  )}
                </div>

                <p className="mt-1 text-sm text-teal-700">
                  Meldinger her vises også i kundeportalen.
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {!bookingDialogOpen && unreadBookingCustomerCount > 0 && (
                  <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {unreadBookingCustomerCount}
                  </span>
                )}
                <span className="text-sm font-medium text-teal-700">
                  {bookingDialogOpen ? "Skjul" : "Vis"}
                </span>
              </div>
            </button>

            {bookingDialogOpen && (
              <div className="space-y-3 border-t border-teal-200 px-4 py-4">
                {bookingMessages.length === 0 ? (
                  <div className="text-sm text-teal-700">Ingen meldinger ennå.</div>
                ) : (
                  <ul className="space-y-2">
                    {bookingMessages.map((msg, idx) => {
                      const isCustomer = msg.from === "customer";
                      const displayName = getFirstName(
                        isCustomer ? msg.fromName || "Kunde" : msg.fromName || "Verksted"
                      );

                      return (
                        <li
                          key={msg._id || `${msg.createdAt}-${idx}`}
                          className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}
                        >
                          <div className="max-w-[80%]">
                            <div
                              className={`mb-1 text-[11px] ${
                                isCustomer ? "text-slate-600" : "text-right text-slate-600"
                              }`}
                            >
                              {displayName || (isCustomer ? "Kunde" : "Verksted")}
                              {msg.createdAt
                                ? ` · ${new Date(msg.createdAt).toLocaleString("no-NO")}`
                                : ""}
                            </div>

                            <div
                              onClick={() => isCustomer && toggleBookingMessageUnread(msg)}
                              title={isCustomer ? "Trykk for å markere som ulest" : ""}
                              className={`rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words transition ${
                                isCustomer
                                  ? msg.readAt
                                    ? "cursor-pointer bg-slate-100 text-slate-900 hover:bg-slate-200"
                                    : "cursor-pointer bg-yellow-100 text-slate-900 hover:bg-yellow-200"
                                  : "bg-indigo-600 text-white"
                              }`}
                            >
                              {msg.text}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="border-t border-teal-200 pt-2">
                  <label className="mb-1 block text-sm text-teal-800 font-medium">Ny melding til kunde</label>

                  <textarea
                    className={`min-h-[100px] w-full rounded-xl border border-teal-300 p-2 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 ${isCancelled ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`}
                    value={bookingMessageText}
                    onChange={(e) => { if (!isCancelled) setBookingMessageText(e.target.value); }}
                    placeholder={isCancelled ? "Kan ikke sende melding til kansellert booking." : "Skriv en melding som kunden kan lese i kundeportalen..."}
                    disabled={isCancelled}
                  />

                  {bookingMessageError ? (
                    <div className="mt-2 text-sm text-red-600">{bookingMessageError}</div>
                  ) : null}

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-600">Kunde ser meldingen i portalen.</div>

                    <button
                      type="button"
                      onClick={sendBookingMessage}
                      disabled={
                        isCancelled || bookingMessageBusy || !bookingMessageText.trim() || !statusActor?.name
                      }
                      className={`rounded-xl px-4 py-2 text-white ${
                        isCancelled || bookingMessageBusy || !bookingMessageText.trim() || !statusActor?.name
                          ? "cursor-not-allowed bg-indigo-400"
                          : "bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    >
                      {bookingMessageBusy ? "Sender..." : "Send melding"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bilder lastet opp av kunde */}
          {Array.isArray(booking?.images) && booking.images.length > 0 && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-teal-200 bg-teal-50">
              <button
                type="button"
                onClick={() => setBookingImagesOpen((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left bg-teal-100 hover:bg-teal-200 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-teal-700 shrink-0">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 className="font-semibold text-teal-900">Bilder fra kunde</h3>
                    <span className="inline-flex items-center rounded-full bg-teal-200 px-2 py-0.5 text-xs text-teal-800">
                      {booking.images.length} bilde{booking.images.length !== 1 ? "r" : ""}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-teal-700">Bilder kunden har lastet opp via kundeportalen.</p>
                </div>
                <span className="shrink-0 text-sm font-medium text-teal-700">
                  {bookingImagesOpen ? "Skjul" : "Vis"}
                </span>
              </button>

              {bookingImagesOpen && (
                <div className="border-t border-teal-200 px-4 py-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {booking.images.map((img, idx) => (
                      <div key={img._id || idx} className="overflow-hidden rounded-xl border bg-white">
                        <a href={img.url} target="_blank" rel="noreferrer" className="block">
                          <img
                            src={img.url}
                            alt={img.originalName || "Kundebilde"}
                            className="w-full h-28 object-cover"
                          />
                        </a>
                        <div className="p-2">
                          {img.originalName && (
                            <div className="text-xs text-slate-700 truncate" title={img.originalName}>
                              {img.originalName}
                            </div>
                          )}
                          {img.uploadedAt && (
                            <div className="text-[11px] text-slate-600 mt-0.5">
                              {new Date(img.uploadedAt).toLocaleString("no-NO")}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mb-4 rounded-lg border bg-white p-4">
            <button
              type="button"
              onClick={() => setDeliveryReminderOpen((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div>
                <h3 className="text-base font-bold text-slate-900">Varsel før innlevering</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Klikk for å {deliveryReminderOpen ? "skjule" : "vise"} innstillinger for
                  påminnelse før innlevering.
                </p>
              </div>

              <span className="shrink-0 text-sm font-medium text-indigo-700">
                {deliveryReminderOpen ? "Skjul" : "Vis"}
              </span>
            </button>

            {deliveryReminderOpen && (
              <div className="mt-4 border-t pt-4">
                {hasActiveDeliveryReminder ? (
                  <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
                    Aktivt innleveringsvarsel:{" "}
                    <span className="font-medium">
                      {formatDateTimeNo(activeDeliveryReminder.sendAt)}
                    </span>
                    {" · "}
                    {activeDeliveryReminder.sms && activeDeliveryReminder.email
                      ? "SMS og e-post"
                      : activeDeliveryReminder.sms
                      ? "SMS"
                      : activeDeliveryReminder.email
                      ? "e-post"
                      : "ingen kanal valgt"}
                  </div>
                ) : null}

                <p className="mb-3 text-sm text-slate-600">
                  Her kan du styre påminnelse til kunden før innleveringsdato.
                </p>

                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="mb-1 font-medium text-slate-800">Foreslått sendetid</div>
                  <div className="text-slate-700">
                    Standard settes til{" "}
                    <span className="font-medium">1 dag før levering kl. 12:00</span>.
                  </div>
                  <div className="mt-1 text-slate-700">
                    Nåværende tidspunkt:{" "}
                    <span className="font-medium">{formatDateTimeNo(deliveryReminderDateTime)}</span>
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm text-slate-600">
                      Sendetidspunkt for innleveringsvarsel
                    </label>
                    <input
                      type="datetime-local"
                      className={`w-full rounded-lg border p-2 ${isCancelled ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`}
                      value={deliveryReminderDateTime}
                      onChange={(e) => { if (!isCancelled) setDeliveryReminderDateTime(e.target.value); }}
                      disabled={isCancelled}
                    />
                    <p className="mt-1 text-xs text-slate-600">
                      Dette tidspunktet kan endres manuelt.
                    </p>

                    <button
                      type="button"
                      onClick={saveDeliveryReminderSettings}
                      disabled={isCancelled || deliveryReminderSaving}
                      className={`mt-3 rounded-xl px-4 py-2 text-white ${
                        isCancelled || deliveryReminderSaving
                          ? "cursor-not-allowed bg-indigo-400"
                          : "bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    >
                      {deliveryReminderSaving ? "Lagrer..." : "Lagre innleveringsvarsel"}
                    </button>

                    {deliveryReminderNotice ? (
                      <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                        {deliveryReminderNotice}
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-3">
                    <label className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${isCancelled ? "cursor-not-allowed opacity-50" : ""}`}>
                      <input
                        type="checkbox"
                        checked={sendDeliveryReminderSms}
                        onChange={(e) => { if (!isCancelled) setSendDeliveryReminderSms(e.target.checked); }}
                        disabled={isCancelled}
                      />
                      <span className="whitespace-nowrap">Send SMS-varsel</span>
                      <span className="min-w-0 break-all text-xs text-slate-600">
                        {hasPhone ? phoneValue : "Mangler telefonnummer"}
                      </span>
                    </label>

                    <label className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${isCancelled ? "cursor-not-allowed opacity-50" : ""}`}>
                      <input
                        type="checkbox"
                        checked={sendDeliveryReminderEmail}
                        onChange={(e) => { if (!isCancelled) setSendDeliveryReminderEmail(e.target.checked); }}
                        disabled={isCancelled}
                      />
                      <span className="whitespace-nowrap">Send e-postvarsel</span>
                      <span className="min-w-0 break-all text-xs text-slate-600">
                        {hasEmail ? emailValue : "Mangler e-postadresse"}
                      </span>
                    </label>


                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-sm font-medium text-slate-800">SMS-forhåndsvisning</div>
                    <pre className="font-sans text-sm whitespace-pre-wrap text-slate-700">
                      {deliveryReminderSmsPreview}
                    </pre>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-sm font-medium text-slate-800">E-post-forhåndsvisning (tekstversjon)</div>
                    <pre className="font-sans text-sm whitespace-pre-wrap text-slate-700">
                      {deliveryReminderEmailPreview}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {portalUrl && (
            <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 basis-60">
                  <h3 className="font-semibold text-indigo-900">Kundeportal</h3>
                  <p className="mt-1 text-sm text-indigo-800">
                    Del denne lenken med kunden for å vise bookingstatus, artikkelinfo, datoer og
                    dialog.
                  </p>

                  <div className="mt-3 break-all text-sm font-medium text-indigo-900">{portalUrl}</div>

                  {booking?.portal?.lastOpenedAt && (
                    <div className="mt-2 text-xs text-indigo-700">
                      Sist åpnet: {new Date(booking.portal.lastOpenedAt).toLocaleString("no-NO")}
                    </div>
                  )}
                </div>

                <div className="flex min-w-0 flex-row flex-wrap gap-2 sm:shrink-0 sm:flex-col">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(portalUrl)}
                    className="rounded-xl bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
                  >
                    Kopier lenke
                  </button>

                  <a
                    href={portalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-indigo-300 bg-white px-3 py-2 text-center text-sm text-indigo-700 hover:bg-indigo-100"
                  >
                    Åpne portal
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {editProductModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
          <div className="border-b px-4 py-3">
            <h4 className="text-lg font-semibold">Rediger produkt</h4>
          </div>

          <div className="space-y-4 p-4">
            {(() => {
              const product = booking?.items?.[editingProductIndex] || {};
              const itemConverted = (booking?.convertedItemIndexes || []).includes(
                editingProductIndex
              );
              const currentSlug = verkstedSlugForProduct(product);
              const currentKnown = VERKSTED_VALG.some((v) => v.slug === currentSlug);
              const moving =
                !itemConverted && !!editVerkstedSlug && editVerkstedSlug !== currentSlug;

              return (
                <div>
                  <label
                    htmlFor="edit-product-verksted"
                    className="mb-1 block text-sm text-slate-600"
                  >
                    Verksted
                  </label>
                  <select
                    id="edit-product-verksted"
                    className="w-full rounded-lg border p-2 disabled:bg-slate-100"
                    value={editVerkstedSlug}
                    onChange={(e) => setEditVerkstedSlug(e.target.value)}
                    disabled={itemConverted || updating}
                  >
                    {!currentKnown && (
                      <option value={currentSlug}>{verkstedNavnForProduct(product)}</option>
                    )}
                    {VERKSTED_VALG.map((v) => (
                      <option key={v.slug} value={v.slug}>
                        {v.navn}
                      </option>
                    ))}
                  </select>
                  {itemConverted ? (
                    <p className="mt-1 text-xs text-slate-600">
                      Produktet er allerede mottatt og kan ikke flyttes herfra.
                    </p>
                  ) : moving ? (
                    <p className="mt-1 text-xs text-amber-800">
                      Flyttes fra {verkstedNavnForProduct(product)} til{" "}
                      {VERKSTED_VALG.find((v) => v.slug === editVerkstedSlug)?.navn}. Datoer og
                      annet innhold beholdes. Send oppdatert bookinginfo til kunden etterpå.
                    </p>
                  ) : null}
                </div>
              );
            })()}

            <div>
              <label className="mb-1 block text-sm text-slate-600">Produktmerke</label>
              <input
                type="text"
                className="w-full rounded-lg border p-2"
                value={editProduktmerke}
                onChange={(e) => setEditProduktmerke(e.target.value)}
                placeholder="Produktmerke"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-600">Farge</label>
              <input
                type="text"
                className="w-full rounded-lg border p-2"
                value={editFarge}
                onChange={(e) => setEditFarge(e.target.value)}
                placeholder="Farge"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-600">Kommentar</label>
              <textarea
                className="min-h-[100px] w-full rounded-lg border p-2"
                value={editKommentar}
                onChange={(e) => setEditKommentar(e.target.value)}
                placeholder="Kommentar"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-600">Leveringsdato</label>
              <input
                type="date"
                className="w-full rounded-lg border p-2"
                value={editLeveringsDato}
                onChange={(e) => setEditLeveringsDato(e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-600">Forventet ferdig-dato</label>
              <input
                type="date"
                className="w-full rounded-lg border p-2"
                value={editForventetFerdigDato}
                onChange={(e) => setEditForventetFerdigDato(e.target.value)}
              />
            </div>

            {booking?.items?.[editingProductIndex]?.ferdigDatoValgfri && (
              <div>
                <label
                  htmlFor="edit-onsket-ferdig-dato"
                  className="mb-1 block text-sm text-slate-600"
                >
                  Ønsket ferdigdato (lovet kunden)
                </label>
                <div className="flex gap-2">
                  <input
                    id="edit-onsket-ferdig-dato"
                    type="date"
                    className="w-full rounded-lg border p-2"
                    value={editOnsketFerdigDato}
                    onChange={(e) => setEditOnsketFerdigDato(e.target.value)}
                    aria-describedby="edit-onsket-ferdig-hint"
                  />
                  {editOnsketFerdigDato && (
                    <button
                      type="button"
                      className="whitespace-nowrap rounded-lg border px-3 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={() => setEditOnsketFerdigDato("")}
                    >
                      Fjern
                    </button>
                  )}
                </div>
                <p id="edit-onsket-ferdig-hint" className="mt-1 text-xs text-slate-600">
                  Tom = «{IKKE_SATT}».
                </p>
                {erForTidligOnsket(editOnsketFerdigDato, editForventetFerdigDato) && (
                  <p role="alert" className="mt-1 text-xs font-medium text-amber-700">
                    Advarsel: ønsket dato er før beregnet ferdigdato. Sjekk kapasiteten
                    før du lover kunden dette.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <button
              type="button"
              className="rounded-lg border px-4 py-2 text-slate-700 hover:bg-slate-50"
              onClick={closeEditProductModal}
              disabled={updating}
            >
              Avbryt
            </button>

            <button
              type="button"
              className={`rounded-lg px-4 py-2 text-white ${
                updating ? "cursor-not-allowed bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
              onClick={saveProductChanges}
              disabled={updating}
            >
              Lagre
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}